import { getAccessToken, setAccessToken } from './authToken';
import type {
  ChangePasswordBody,
  ForgotPasswordBody,
  LoginBody,
  LoginResponse,
  RefreshResponse,
  ResetPasswordBody,
  Role,
  User,
} from './types';
import type { UploadSlotKey, Uploads } from './declared-fields';
import type { ContractField } from './contract-fields';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

// ── Refresh token singleton guard ────────────────────────────────────────────
// Prevents multiple concurrent /auth/refresh requests when several calls 401
// at once (refresh token lives in an httpOnly cookie, sent via credentials).
let _refreshing: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) {
      setAccessToken(null);
      return null;
    }
    const data = (await res.json()) as RefreshResponse;
    setAccessToken(data.accessToken);
    return data.accessToken;
  } catch {
    setAccessToken(null);
    return null;
  }
}

// ── Cross-tab refresh coordination ──────────────────────────────────────────
// All tabs on the origin share ONE rotating refresh cookie. If each tab's
// proactive timer (or reactive 401) fires its own /auth/refresh at the same
// moment, the server sees its just-rotated token re-presented and revokes the
// whole token family (reuse detection) -> every tab 401s and bounces to
// /login. Fix: elect a single "leader" tab per refresh via the Web Locks API
// (ifAvailable — never blocks); the leader does the real network call and
// broadcasts the result over BroadcastChannel, other tabs adopt it instead of
// racing the network. Both APIs are feature-detected: unavailable in an
// environment (older browser, jsdom/vitest, SSR) -> falls straight back to
// doRefresh(), the pre-existing behavior.
const REFRESH_LOCK_NAME = 'egc-token-refresh';
const AUTH_CHANNEL_NAME = 'egc-auth';

const authChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(AUTH_CHANNEL_NAME) : null;

// Tabs that lost the leader-election race for the in-flight refresh wait here
// for the leader's broadcast rather than also hitting the network.
let refreshWaiters: Array<(token: string | null) => void> = [];

// Broadcast payload is just the new access token (or null on a failed refresh)
// — nothing else needs to cross the channel.
authChannel?.addEventListener('message', (ev: MessageEvent<string | null>) => {
  const token = ev.data;
  setAccessToken(token);
  const waiters = refreshWaiters;
  refreshWaiters = [];
  waiters.forEach((resolve) => resolve(token));
});

const LEADER_WAIT_TIMEOUT = Symbol('leader-wait-timeout');

// D92 — was 5000ms, which is how long a hard-refreshed tab used to block on a
// broadcast that an idle lock-holder was never going to send (see
// coordinatedRefresh). This is only ever a best-effort head start for a leader
// that IS genuinely mid-flight, so it needs to be comfortably longer than a
// refresh round-trip and comfortably shorter than a user noticing a stall.
// Exported so the test asserts against this value rather than restating it —
// same convention as FilterBar's SEARCH_DEBOUNCE_MS (D79).
export const LEADER_WAIT_MS = 400;

/** Waits for the refresh leader's broadcast. Times out (falls back to a direct refresh) if the leader tab vanishes mid-flight, e.g. closed. */
function waitForLeaderBroadcast(): Promise<string | null | typeof LEADER_WAIT_TIMEOUT> {
  return new Promise((resolve) => {
    const onToken = (token: string | null) => {
      clearTimeout(timer);
      resolve(token);
    };
    const timer = setTimeout(() => {
      refreshWaiters = refreshWaiters.filter((w) => w !== onToken);
      resolve(LEADER_WAIT_TIMEOUT);
    }, LEADER_WAIT_MS);
    refreshWaiters.push(onToken);
  });
}

/** Only the elected leader tab calls doRefresh(); everyone else adopts its broadcast. */
async function coordinatedRefresh(): Promise<string | null> {
  if (!authChannel || typeof navigator === 'undefined' || !navigator.locks) {
    return doRefresh();
  }
  let isLeader = false;
  const leaderResult = await navigator.locks.request(REFRESH_LOCK_NAME, { ifAvailable: true }, async (lock) => {
    if (!lock) return null; // another tab already holds it — don't duplicate the network call
    isLeader = true;
    const token = await doRefresh();
    authChannel.postMessage(token);
    return token;
  });
  if (isLeader) return leaderResult;

  // D92 (Rob Morris, 2026-09-15): "when i hard refresh it directs me to login page."
  //
  // THE BUG WAS HERE. Losing the lock does NOT mean another tab is mid-refresh
  // — `ifAvailable: true` also returns null when a merely IDLE tab happens to
  // hold the lock, and an idle tab never calls doRefresh() and therefore never
  // broadcasts. So a hard-refreshed tab (whose access token is in-memory only,
  // and therefore always gone after a reload — see authToken.ts) elected itself
  // a non-leader and then sat in waitForLeaderBroadcast() for the FULL 5s
  // timeout waiting on a message nobody was ever going to send, before finally
  // falling back to the network. Five seconds of spinner on every reload, and
  // any hiccup in that window put the user on /login with their session still
  // perfectly valid in the cookie.
  //
  // The wait is now a short OPTIMISATION, not a dependency: give a genuine
  // in-flight leader a brief moment to broadcast (which costs one duplicated
  // network call at most if we outrun it), then just do the refresh ourselves.
  // Correctness no longer depends on a broadcast that may never arrive — the
  // reuse-detection race the lock exists to prevent is still avoided in the
  // common case, and a rare duplicate refresh is far cheaper than a spurious
  // logout.
  const awaited = await waitForLeaderBroadcast();
  return awaited === LEADER_WAIT_TIMEOUT ? doRefresh() : awaited;
}

/** Exported so AuthProvider's proactive refresh timer shares the same singleton guard as the reactive 401 path. */
export function refreshOnce(): Promise<string | null> {
  if (!_refreshing) {
    _refreshing = coordinatedRefresh().finally(() => {
      _refreshing = null;
    });
  }
  return _refreshing;
}

function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Never redirect while already on /login — the auth bootstrap (GET /api/auth/me
// with no token) 401s on the login page itself; without this guard a hard
// navigation would reload the page and re-trigger the same 401 in a loop.
//
// Every call site below reaches this only after a 401 AND a failed refresh —
// i.e. a session that was live moments ago just died mid-action (e.g. an
// approve/reject 401s and can't recover). ?sessionExpired=1 survives the
// hard navigation (a router `state` object would not) so LoginPage can tell
// the user their last action may not have gone through, instead of bouncing
// them to a blank sign-in form with no explanation.
function redirectToLogin(): void {
  if (window.location.pathname !== '/login') {
    window.location.href = '/login?sessionExpired=1';
  }
}

// Wave 5: the backend enforces PASSWORD_CHANGE_REQUIRED (403) on every
// authenticated route except /auth/me,/change-password,/logout,/refresh
// while mustChangePassword is true. RequireAuth already renders
// ChangePasswordPage from user.mustChangePassword — a stray 403 here means
// some other authenticated call raced ahead of that gate (e.g. right after
// a password change, before the old access token is discarded). A reload
// re-runs AuthProvider's getMe() bootstrap (an exempt route) so RequireAuth
// picks up the current mustChangePassword and shows the right screen,
// without wiring a cross-cutting pub/sub just for this edge case.
function forcePasswordChange(): void {
  window.location.reload();
}

// F5 (class fix — 3rd recurrence: retired per-page at OnboardPage r3 and
// SigningPage r7, now fixed once here instead). A malformed id/slug can miss
// every app route and fall through to Nest's own unmatched-route handler —
// but AllExceptionsFilter still wraps that in valid JSON, so res.json()
// succeeds and Express's boilerplate "Cannot GET /path" rides through as
// body.message. That fallback path also gets a `code` (synthesized as
// HTTP_<status>), same as e.g. class-validator's array-of-errors response —
// so `code` can't distinguish framework noise from a message the API meant
// to send. The message text itself is the only reliable signal: nothing this
// app deliberately authors is shaped like "Cannot <METHOD> <path>".
const RAW_FRAMEWORK_MESSAGE_RE = /^Cannot [A-Z]+ /;
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';
const NETWORK_ERROR_MESSAGE = 'Network error. Check your connection and try again.';

/** The message as-is if it's something the API meant to send, else undefined. */
function safeErrorMessage(message: string | undefined): string | undefined {
  return message && !RAW_FRAMEWORK_MESSAGE_RE.test(message) ? message : undefined;
}

/** Wraps every fetch() below — a rejected promise here means the request never
 * reached a server (offline, DNS, CORS): no Response to parse, so the raw
 * browser text (e.g. "Failed to fetch") must never reach the UI either. */
async function safeFetch(input: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch {
    throw new ApiError(NETWORK_ERROR_MESSAGE, 'NETWORK_ERROR');
  }
}

async function extractErrorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { message?: string };
    return safeErrorMessage(body?.message) ?? GENERIC_ERROR_MESSAGE;
  } catch {
    return `HTTP ${res.status}`;
  }
}

/** GET with bearer auth, 401 → single refresh retry → redirect to /login. */
export async function request<T>(path: string, retried = false): Promise<T> {
  const res = await safeFetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { ...authHeaders() },
  });
  if (res.status === 401 && !retried) {
    const newToken = await refreshOnce();
    if (!newToken) {
      redirectToLogin();
      throw new Error('Unauthenticated');
    }
    return request<T>(path, true);
  }
  if (!res.ok) throw new Error(await extractErrorMessage(res));
  return res.json() as Promise<T>;
}

/** POST/PATCH/DELETE with a JSON body, same 401-retry contract as request(). */
async function writeRequest<T>(
  method: 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  retried = false,
): Promise<T> {
  const res = await safeFetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 && !retried) {
    const newToken = await refreshOnce();
    if (!newToken) {
      redirectToLogin();
      throw new Error('Unauthenticated');
    }
    return writeRequest<T>(method, path, body, true);
  }
  if (!res.ok) throw new Error(await extractErrorMessage(res));
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

export const postRequest = <T>(path: string, body?: unknown) => writeRequest<T>('POST', path, body);
export const patchRequest = <T>(path: string, body?: unknown) => writeRequest<T>('PATCH', path, body);
export const deleteRequest = <T>(path: string) => writeRequest<T>('DELETE', path);

/** Builds a `?a=b&c=d` query string from a filters object, skipping undefined/empty values. Returns '' when nothing is set. */
export function buildListQuery(params: Record<string, string | undefined>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) usp.set(key, value);
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function login(body: LoginBody): Promise<LoginResponse> {
  const res = await safeFetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res));
  const data = (await res.json()) as LoginResponse;
  setAccessToken(data.accessToken);
  return data;
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...authHeaders() },
    });
  } catch {
    // Best-effort — clear token regardless
  }
  setAccessToken(null);
}

export async function getMe(): Promise<User> {
  return request<User>('/api/auth/me');
}

// ── Wave 2: campaigns, vendors, contracts (Sprint 1+2, frontend track) ──────
//
// error envelope is {statusCode,message,code,extra?}; ApiError below carries
// the parsed `code` + full body so callers can branch on e.g.
// TEMPLATE_VALIDATION_FAILED's `report` or INVALID_COMP_VALUES without a
// second round of string-matching on `message` (extractErrorMessage above
// only returns the message and is left untouched — Wave 1 code depends on
// its exact behavior).

export class ApiError extends Error {
  code?: string;
  body?: Record<string, unknown>;
  constructor(message: string, code?: string, body?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.body = body;
  }
}

async function parseApiError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as Record<string, unknown>;
    const code = body?.code as string | undefined;
    if (res.status === 403 && code === 'PASSWORD_CHANGE_REQUIRED') forcePasswordChange();
    const rawMessage = typeof body?.message === 'string' ? body.message : undefined;
    // code/body pass through untouched — existing page-level 404 checks read
    // err.body?.statusCode, not err.message. Only the message is ever masked.
    return new ApiError(safeErrorMessage(rawMessage) ?? GENERIC_ERROR_MESSAGE, code, body);
  } catch {
    return new ApiError(`HTTP ${res.status}`);
  }
}

/** Same 401-retry contract as request()/writeRequest(), full ApiError on failure. */
async function apiRequest<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  retried = false,
): Promise<T> {
  const res = await safeFetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 && !retried) {
    const newToken = await refreshOnce();
    if (!newToken) {
      redirectToLogin();
      throw new Error('Unauthenticated');
    }
    return apiRequest<T>(method, path, body, true);
  }
  if (!res.ok) throw await parseApiError(res);
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

/** Multipart POST/PUT (template/resource/signature upload) — same 401-retry + ApiError contract. */
async function apiPostForm<T>(
  path: string,
  form: FormData,
  method: 'POST' | 'PUT' = 'POST',
  retried = false,
): Promise<T> {
  const res = await safeFetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    headers: { ...authHeaders() }, // no Content-Type — browser sets the multipart boundary
    body: form,
  });
  if (res.status === 401 && !retried) {
    const newToken = await refreshOnce();
    if (!newToken) {
      redirectToLogin();
      throw new Error('Unauthenticated');
    }
    return apiPostForm<T>(path, form, method, true);
  }
  if (!res.ok) throw await parseApiError(res);
  return res.json() as Promise<T>;
}

/** Authenticated binary GET (PDF preview) — same 401-retry + ApiError contract. */
async function apiGetBlob(path: string, retried = false): Promise<Blob> {
  const res = await safeFetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { ...authHeaders() },
  });
  if (res.status === 401 && !retried) {
    const newToken = await refreshOnce();
    if (!newToken) {
      redirectToLogin();
      throw new Error('Unauthenticated');
    }
    return apiGetBlob(path, true);
  }
  if (!res.ok) throw await parseApiError(res);
  return res.blob();
}

/** Pulls the filename out of a `Content-Disposition: attachment; filename="..."` header, if present. */
function filenameFromContentDisposition(header: string | null): string | undefined {
  if (!header) return undefined;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Authenticated binary GET that also surfaces the server's suggested filename
 * (Content-Disposition) — same 401-retry/ApiError contract as apiGetBlob,
 * used where the caller wants the server-chosen name rather than building its
 * own (client-portal-bundle zip).
 */
async function apiGetBlobWithFilename(path: string, retried = false): Promise<{ blob: Blob; filename?: string }> {
  const res = await safeFetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { ...authHeaders() },
  });
  if (res.status === 401 && !retried) {
    const newToken = await refreshOnce();
    if (!newToken) {
      redirectToLogin();
      throw new Error('Unauthenticated');
    }
    return apiGetBlobWithFilename(path, true);
  }
  if (!res.ok) throw await parseApiError(res);
  const filename = filenameFromContentDisposition(res.headers.get('Content-Disposition'));
  const blob = await res.blob();
  return { blob, filename };
}

/**
 * Authenticated binary POST (preparation preview render) — same 401-retry +
 * ApiError contract as apiGetBlob. `body` is optional and additive (D74): the
 * original preparation-preview call site sends no body at all, while
 * generateBadgePreview needs a JSON `{ campaignId, submissionId,
 * badgeNumberOverride? }` payload — same Content-Type-only-when-present shape
 * apiRequest already uses above.
 */
async function apiPostBlob(path: string, body?: unknown, retried = false): Promise<Blob> {
  const res = await safeFetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...authHeaders() },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 && !retried) {
    const newToken = await refreshOnce();
    if (!newToken) {
      redirectToLogin();
      throw new Error('Unauthenticated');
    }
    return apiPostBlob(path, body, true);
  }
  if (!res.ok) throw await parseApiError(res);
  return res.blob();
}

// Wave 5: forces a themed change-password screen for seeded staff carrying
// mustChangePassword:true. Returns 204 No Content on success — the backend
// revokes all refresh tokens and clears the refresh cookie, so the caller
// must clear the local session and send the user back through /login rather
// than trying to continue on the now-revoked session.
export const changePassword = (dto: ChangePasswordBody): Promise<void> =>
  apiRequest<void>('POST', '/api/auth/change-password', dto);

// D26 self-service forgot/reset password — both @Public(), plain-fetch
// contract (same reasoning as getSignToken/getOnboardContext: no session
// exists yet on these pages, so apiRequest's 401-redirect-to-login path
// would be wrong here). Always resolves 200 {ok:true} — never reveals
// whether the email exists.
export async function requestPasswordReset(dto: ForgotPasswordBody): Promise<{ ok: true }> {
  const res = await safeFetch(`${API_BASE}/api/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json() as Promise<{ ok: true }>;
}

// 400 on invalid/expired/used token or weak password, via ApiError.
export async function resetPassword(dto: ResetPasswordBody): Promise<void> {
  const res = await safeFetch(`${API_BASE}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
  });
  if (!res.ok) throw await parseApiError(res);
}

// ── Campaigns ────────────────────────────────────────────────────────────────

export type Campaign = {
  id: string;
  name: string;
  product: string;
  active: boolean;
  compLabels: string[];
  // A4/D-3 (2026-07-27): sole resolution axis. 2-letter code, the 'ALL' all-states
  // sentinel, or null (no state yet — CAMPAIGN_STATE_MISSING on generate).
  state: string | null;
  // S49: typed signer name stamped on this campaign's contracts; null = default EGC signer (Settings).
  contractSignerName?: string | null;
  archivedAt: string | null;
  // S39/D5x: campaign-level override for where rep-submission completion
  // emails go. Disabled or blank email falls back to the global default
  // (contact@egc-inc.com).
  submissionRecipientEnabled: boolean;
  submissionRecipientEmail: string | null;
  // S39/D94: per-campaign communication email (sender) override. senderHasPassword
  // reports whether an SMTP password is stored server-side — the password itself
  // is write-only and never returned on the wire.
  senderEnabled: boolean;
  senderFromName: string | null;
  senderFromEmail: string | null;
  senderReplyTo: string | null;
  senderSmtpHost: string | null;
  senderSmtpPort: number | null;
  senderSmtpUser: string | null;
  senderHasPassword: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CampaignInput = {
  name: string;
  product: string;
  active?: boolean;
  compLabels: string[];
  // Optional, 2-letter code, the 'ALL' all-states sentinel, or omitted/undefined (D-3).
  state?: string;
  // S49: '' clears back to the default EGC signer.
  contractSignerName?: string;
  // S11 hardening — update() only: set true to proceed after the admin has
  // explicitly acknowledged a 409 STATE_CHANGE_CONFIRMATION_REQUIRED (switching
  // to ALL would orphan existing state-scoped documents). Ignored on create
  // (there's no prior state to compare) and on any other update.
  confirmStateChange?: boolean;
};

// ARCH §1.C2 — PDF uploads skip docxtemplater validation entirely and return a
// distinct summary shape (no checks/missingTags/missingAnchors/errors). Union
// keeps both wire shapes visible to callers instead of only modeling DOCX.
export type ValidationReport =
  | {
      valid: boolean;
      checks: {
        magicBytes: boolean;
        requiredTags: boolean;
        compsLoop: boolean;
        docusealAnchors: boolean;
        dryRunRender: boolean;
      };
      missingTags: string[];
      missingAnchors: string[];
      errors: string[];
    }
  | { valid: true; format: 'pdf'; pageCount: number };

// S15 (SPEC-2026-08-04): PENDING_PREPARATION = accepted-and-held untagged
// contract; READY = generation-eligible. Present on every TemplateVersion —
// server field has no @default (fail-closed), so this is never optional here.
export type TemplatePreparationStatus = 'PENDING_PREPARATION' | 'READY';

export type TemplateVersion = {
  id: string;
  campaignId: string;
  // Free-form display label post-migration (ARCH §4.4); 'CONTRACT' reserved. No
  // enum literals rendered anywhere — the kind string IS the label.
  kind: string;
  state: string | null;
  preparationStatus: TemplatePreparationStatus;
  // ARCH §4.5 (S11 B4/C): additive list-item fields. signatureFields raw JSON is
  // NOT included here — only on the single-version admin fetch (future C6 use).
  channel: string | null;
  signReady: boolean;
  docusealTemplateId: number | null;
  pageCount: number | null;
  archivedAt: string | null;
  version: number;
  documentId: string;
  // ARCH §4.1 (T1.2 staleness aid): sha256 of the stored Document blob.
  sha256: string;
  // Version number this upload is byte-identical to (same sha256), or null.
  // Server always creates the new version anyway (never a silent no-op) —
  // this just tells the admin they probably didn't mean to re-upload.
  duplicateOfVersion: number | null;
  validation: ValidationReport;
  createdAt: string;
};

export type CampaignListFilters = { includeArchived?: boolean; q?: string; dateFrom?: string; dateTo?: string };

export const listCampaigns = (filters: CampaignListFilters = {}) => {
  const { includeArchived, ...rest } = filters;
  const qs = buildListQuery({ ...rest, includeArchived: includeArchived ? 'true' : undefined });
  return apiRequest<Campaign[]>('GET', `/api/campaigns${qs}`);
};
// F5-class fix (2nd instance): id comes straight from useParams — a raw '/'
// (e.g. decoded from a %2F inside a <script>-shaped url probe) splits this
// into extra path segments and misses the :id route entirely, landing on
// Express's "Cannot GET" catch-all instead of a real 404. encodeURIComponent
// guarantees exactly one path segment reaches the backend either way,
// including when useParams hands back an undecoded string because the
// segment was itself a malformed %-escape (react-router's decode already
// failed once — re-encoding it here is what makes it decodeURIComponent
// cleanly server-side instead of 400ing on the same bad escape again).
// F5-class fix (4th instance) — a '/' can never appear in a real single-
// segment id (cuid-style), so a slash-bearing id (e.g. <script>...</script>,
// which contains one in its closing tag) is never going to resolve no matter
// how it's encoded: nginx decodes %2F back to a literal '/' before Express
// routes it, splitting the path and missing :id regardless. Reject it before
// the request fires — same "id can't be valid, don't even ask" guard
// OnboardPage.tsx already applies to its :slug param — instead of sending a
// doomed request whose failure mode (raw "Cannot GET" 404) is misleading.
// encodeURIComponent below still stands: it's what fixes every OTHER
// odd-but-single-segment id (e.g. a malformed %-escape).
export const getCampaign = (id: string) =>
  id.includes('/')
    ? Promise.reject(new Error('Campaign not found'))
    : apiRequest<Campaign>('GET', `/api/campaigns/${encodeURIComponent(id)}`);
export const createCampaign = (dto: CampaignInput) => apiRequest<Campaign>('POST', '/api/campaigns', dto);
export const updateCampaign = (id: string, dto: Partial<CampaignInput>) =>
  apiRequest<Campaign>('PATCH', `/api/campaigns/${id}`, dto);
export const deactivateCampaign = (id: string) => apiRequest<Campaign>('POST', `/api/campaigns/${id}/deactivate`);
export const archiveCampaign = (id: string) => apiRequest<Campaign>('POST', `/api/campaigns/${id}/archive`);
// R28 archive-visibility close — undoes archiveCampaign; same response shape.
export const restoreCampaign = (id: string) => apiRequest<Campaign>('POST', `/api/campaigns/${id}/restore`);

// S39/D5x: campaign-level override for where rep-submission completion
// emails go. Mirrors ContractExportRecipientsInput/setContractExportRecipients.
export type CampaignSubmissionRecipientInput = {
  submissionRecipientEnabled?: boolean;
  submissionRecipientEmail?: string | null;
};

export function setCampaignSubmissionRecipient(id: string, input: CampaignSubmissionRecipientInput) {
  return apiRequest<Campaign>('PATCH', `/api/campaigns/${id}/submission-recipient`, input);
}

// S39/D94: per-campaign communication email (sender). Partial so a save can
// send only the touched keys (e.g. omit senderSmtpPassword when untouched).
export type CampaignSenderInput = Partial<{
  senderEnabled: boolean;
  senderFromName: string | null;
  senderFromEmail: string | null;
  senderReplyTo: string | null;
  senderSmtpHost: string | null;
  senderSmtpPort: number | null;
  senderSmtpUser: string | null;
  senderSmtpPassword: string | null;
}>;

export function setCampaignSender(id: string, input: CampaignSenderInput) {
  return apiRequest<Campaign>('PATCH', `/api/campaigns/${id}/sender`, input);
}

export type CampaignSenderStatus = {
  configured: boolean;
  source: 'campaign' | 'setting' | 'env' | null;
  fromEmail: string | null;
};

export function getCampaignSenderStatus(id: string) {
  return apiRequest<CampaignSenderStatus>('GET', `/api/campaigns/${id}/sender/status`);
}

export function sendCampaignSenderTest(id: string) {
  return apiRequest<{ delivered: boolean; reason?: string; source: string }>(
    'POST',
    `/api/campaigns/${id}/sender/test`,
  );
}

// D-2 — clone copies product + compLabels only (comps differ per state); nothing else.
export type CloneCampaignInput = { name: string; state: string };
export const cloneCampaign = (id: string, dto: CloneCampaignInput) =>
  apiRequest<Campaign>('POST', `/api/campaigns/${id}/clone`, dto);

// ── D72 stage 1 — per-campaign badge settings (template upload + vendor id).
// Generation/preview/email are stages 2/3, not built here — see
// .claude/SCOPE-badge-generator.md. ──────────────────────────────────────

export type BadgeTemplateInfo = { documentId: string; sizeBytes: number; mimeType: string; uploadedAt: string };
export type BadgeSettings = {
  campaignId: string;
  badgeTemplateDocumentId: string | null;
  badgeVendorId: string | null;
  // null = "No badge template" — the expected default for most campaigns.
  badgeTemplate: BadgeTemplateInfo | null;
};

export const getBadgeSettings = (campaignId: string) =>
  apiRequest<BadgeSettings>('GET', `/api/campaigns/${campaignId}/badge-settings`);

export const setBadgeVendorId = (campaignId: string, badgeVendorId: string | null) =>
  apiRequest<BadgeSettings>('PATCH', `/api/campaigns/${campaignId}/badge-settings/vendor-id`, { badgeVendorId });

export function uploadBadgeTemplate(campaignId: string, file: File): Promise<BadgeSettings> {
  const form = new FormData();
  form.append('file', file);
  return apiPostForm(`/api/campaigns/${campaignId}/badge-settings/template`, form);
}

export const fetchBadgeTemplatePdf = (campaignId: string) =>
  apiGetBlob(`/api/campaigns/${campaignId}/badge-settings/template`);

// ── D74 stage 3 — badge placement editor + issue/send client (web) ─────────
// Additive only, next to the D72 stage-1 badge-settings block above.
// Route shapes are frozen against the sprint plan's atomic contract (A8):
// GET/PUT badge-placements and POST /api/badges/preview were built in D73
// stage 2 and are unmodified here; /api/badges/recipients and
// /api/badges/issue are new this sprint (backend built in parallel against
// these same frozen signatures).

// Mirrors apps/api/src/badges/badge-placement.types.ts's BadgePlacement /
// BadgePlacementSlot — hand-kept in sync (same convention as
// lib/declared-fields.ts's own mirror-of-the-server-types header). Normalized
// 0..1, top-left origin, 0-based page index — the repo's one coordinate
// scheme.
export type BadgePlacementSlot = 'name' | 'badgeNumber' | 'headshot' | 'vendorId';

// D76 (Rob Morris, after generating a real Wave badge): both fields OPTIONAL,
// same "unset = today's default behavior" convention the server's own header
// documents (badge-placement.types.ts) — never send `0`/`undefined` as if
// they mean different things, `undefined` is just "no override".
//   - `rotation`: headshot-slot-only manual override, one of the four values
//     a JPEG's own EXIF orientation tag can encode without also mirroring.
//     The stamper already reads EXIF automatically; this is the escape hatch
//     for a photo whose EXIF is missing or wrong.
//   - `fontSize`: text-slot-only (name/badgeNumber/vendorId) manual target
//     size in points, 4-72 inclusive (BADGE_FONT_SIZE_MIN_PT/MAX_PT on the
//     server). It raises the starting size the stamper's fitFontSize shrinks
//     down from — a ceiling, not a guarantee; the text still shrinks to fit
//     its box rather than overflow it.
export type BadgeRotation = 0 | 90 | 180 | 270;
export const BADGE_ROTATIONS: readonly BadgeRotation[] = [0, 90, 180, 270];
export const BADGE_FONT_SIZE_MIN_PT = 4;
export const BADGE_FONT_SIZE_MAX_PT = 72;

export type BadgePlacement = {
  slot: BadgePlacementSlot;
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  /** D76: headshot-only manual rotation override. Unset = use EXIF / leave unrotated. */
  rotation?: BadgeRotation;
  /** D76: text-slot-only manual target font size in points (4-72). Unset = automatic (today's behavior). */
  fontSize?: number;
};

export const getBadgePlacements = (campaignId: string) =>
  apiRequest<{ badgeFieldPlacements: BadgePlacement[] }>('GET', `/api/campaigns/${encodeURIComponent(campaignId)}/badge-placements`);

export const putBadgePlacements = (campaignId: string, badgeFieldPlacements: BadgePlacement[]) =>
  apiRequest<{ badgeFieldPlacements: BadgePlacement[] }>(
    'PUT',
    `/api/campaigns/${encodeURIComponent(campaignId)}/badge-placements`,
    { badgeFieldPlacements },
  );

// R3 — managerEmail/managerName come from the submission's contract.vendor
// signer; teamLeadName is display-only context next to the manager checkbox
// (never the email destination itself — see OnboarderBadgeCell).
export type BadgeRecipients = {
  submissionId: string;
  repName: string;
  agentEmail: string;
  managerEmail: string | null;
  managerName: string | null;
  teamLeadName: string | null;
  badgeNumber: string;
  hasBadgeOnFile: boolean;
};

export const getBadgeRecipients = (campaignId: string, submissionId: string) =>
  apiRequest<BadgeRecipients>(
    'GET',
    `/api/badges/recipients?campaignId=${encodeURIComponent(campaignId)}&submissionId=${encodeURIComponent(submissionId)}`,
  );

// POST /api/badges/preview — same authenticated-binary-POST shape as the
// preparation preview render (apiPostBlob), returning the stamped PDF bytes
// for on-screen preview before anything is issued/sent.
export const generateBadgePreview = (body: { campaignId: string; submissionId: string; badgeNumberOverride?: string; rotation?: 0 | 90 | 180 | 270 }): Promise<Blob> =>
  apiPostBlob('/api/badges/preview', body);

// D93 (Rob Morris, 2026-09-15) — bulk document download. POST the selected
// submissionIds, get back a ZIP blob (folder per person: headshot, DL,
// code-of-conduct pages). Same apiPostBlob contract as the badge preview.
export const downloadOnboarderDocuments = (submissionIds: string[]): Promise<Blob> =>
  apiPostBlob('/api/onboarders/documents/bulk-download', { submissionIds });

export type BadgeDelivery = { recipient: 'agent' | 'manager'; address: string; delivered: boolean; reason?: string };
export type IssueBadgeResult = {
  documentId: string;
  previousBadgeDocumentId: string | null;
  sentToAgent: boolean;
  sentToManager: boolean;
  agentEmail: string | null;
  managerEmail: string | null;
  deliveries: BadgeDelivery[];
};

export const issueBadge = (body: {
  campaignId: string;
  submissionId: string;
  badgeNumberOverride?: string;
  rotation?: 0 | 90 | 180 | 270;
  sendToAgent: boolean;
  sendToManager: boolean;
  // D75 — a one-off per-badge CC, typed at send time (not a stored setting).
  // Optional; omitted/empty means no CC.
  cc?: string[];
}): Promise<IssueBadgeResult> => apiRequest<IssueBadgeResult>('POST', '/api/badges/issue', body);

// GET /api/badges/issued/:submissionId — streams the currently-stored badge
// PDF (same apiGetBlob 401-retry/ApiError contract as fetchBadgeTemplatePdf).
export const fetchIssuedBadgePdf = (submissionId: string): Promise<Blob> =>
  apiGetBlob(`/api/badges/issued/${encodeURIComponent(submissionId)}`);

// veritas-report-receiver (D107) — GET /api/onboarders/:submissionId/veritas-report
// streams the LATEST version of the Veritas background-check report PDF.
// Same apiGetBlob 401-retry/ApiError/scope-checked-404 contract as every
// other authenticated document download in this file.
export const fetchVeritasReportPdf = (submissionId: string): Promise<Blob> =>
  apiGetBlob(`/api/onboarders/${encodeURIComponent(submissionId)}/veritas-report`);

// client-portal-bundle — GET /api/onboarders/:submissionId/client-portal-bundle
// streams a zip of headshot/DL/background-check so an admin or staffer can
// hand it straight to the client's own portal upload. Admin and Staff can
// call it. Uses apiGetBlobWithFilename (rather than plain apiGetBlob) so the
// caller can honor the server's "<Last>, <First>.zip" Content-Disposition
// name instead of constructing its own.
export const fetchClientPortalBundle = (submissionId: string): Promise<{ blob: Blob; filename?: string }> =>
  apiGetBlobWithFilename(`/api/onboarders/${encodeURIComponent(submissionId)}/client-portal-bundle`);

// D-1 — derived readiness (never a stored boolean): a campaign isn't usable until
// its documents are assigned AND their fields confirmed. ready === blockers.length === 0.
export type ReadinessBlockerCode =
  | 'NO_STATE'
  | 'NO_DOCUMENTS'
  | 'NOT_SIGN_READY'
  | 'FIELDS_NOT_CONFIRMED'
  | 'NO_CONTRACT_TEMPLATE'
  | 'RESOURCE_NOT_ATTACHED'
  // S15 — latest non-archived CONTRACT version exists but isn't READY yet.
  | 'CONTRACT_BEING_PREPARED';
// D55b (this agent): mirrors apps/api/src/campaigns/readiness.service.ts's
// `documents?: string[]` addition — offending document label(s) for
// per-document blocker codes (currently RESOURCE_NOT_ATTACHED only), e.g.
// ["Back Ground Release Form (v3)", "CONTRACT (v2)"]. Optional + additive:
// an older API response simply omits it.
export type ReadinessBlocker = { code: ReadinessBlockerCode; kind?: string; documents?: string[] };
// S11 hardening — non-blocking (never affects `ready`): flags TemplateVersion
// rows left behind (state-scoped, now unreachable) when an existing per-state
// campaign is switched to ALL_STATES_SENTINEL. See readiness.service.ts.
export type ReadinessAdvisoryCode = 'ALL_STATES_ORPHANED_STATE_DOCS';
export type ReadinessAdvisory = { code: ReadinessAdvisoryCode; state: string; count: number };
export type ReadinessDocument = {
  templateVersionId: string;
  kind: string;
  state: string | null;
  channel: string | null;
  version: number;
  resourceId: string | null;
  signReady: boolean;
  fieldsConfirmed: boolean;
  docusealTemplateId: number | null;
  docusealEditUrl: string | null;
};
export type CampaignReadiness = {
  campaignId: string;
  ready: boolean;
  state: string | null;
  blockers: ReadinessBlocker[];
  advisories: ReadinessAdvisory[];
  documents: ReadinessDocument[];
};
export const getCampaignReadiness = (id: string) =>
  apiRequest<CampaignReadiness>('GET', `/api/campaigns/${id}/readiness`);

export type TemplateListFilters = { includeArchived?: boolean };

export const listTemplates = (campaignId: string, filters: TemplateListFilters = {}) => {
  const qs = buildListQuery({ includeArchived: filters.includeArchived ? 'true' : undefined });
  return apiRequest<TemplateVersion[]>('GET', `/api/campaigns/${campaignId}/templates${qs}`);
};

// ARCH §4.5 (S11 A/B5 suggestion source) — distinct non-null channels for the campaign.
export const getCampaignChannels = (campaignId: string) =>
  apiRequest<string[]>('GET', `/api/campaigns/${campaignId}/channels`);

export function uploadTemplate(
  campaignId: string,
  file: File,
  opts?: { kind?: string; state?: string; channel?: string },
): Promise<{ templateVersion: TemplateVersion; validation: ValidationReport }> {
  const form = new FormData();
  form.append('file', file);
  if (opts?.kind) form.append('kind', opts.kind);
  if (opts?.state) form.append('state', opts.state);
  if (opts?.channel) form.append('channel', opts.channel);
  return apiPostForm(`/api/campaigns/${campaignId}/templates`, form);
}

export const fetchTemplatePreviewPdf = (templateVersionId: string) =>
  apiGetBlob(`/api/templates/${templateVersionId}/preview`);

export const archiveTemplate = (templateVersionId: string) =>
  apiRequest<TemplateVersion>('POST', `/api/templates/${templateVersionId}/archive`);
// R28 archive-visibility close — undoes archiveTemplate; same response shape.
export const restoreTemplateVersion = (templateVersionId: string) =>
  apiRequest<TemplateVersion>('POST', `/api/templates/${templateVersionId}/restore`);

// ── S12 — dynamic intake fields (ARCH §C2/§C4/§C6, FROZEN) ──────────────────

// 'ssn' (D-9): distinguished from generic text — FE renders a masked input,
// API validates format and strips the value from everything persisted/emailed/logged.
export type IntakeFieldType = 'text' | 'email' | 'phone' | 'number' | 'date' | 'select' | 'checkbox' | 'ssn';
export type IntakeField = { key: string; label: string; type: IntakeFieldType; required: boolean; options?: string[] };
export type IntakeFieldSetRow = {
  id: string;
  campaignId: string;
  state: string | null;
  channel: string | null;
  fields: IntakeField[];
  createdAt: string;
  updatedAt: string;
};
export type UpsertIntakeFieldSetInput = { state?: string | null; channel?: string | null; fields: IntakeField[] };

export const listIntakeFieldSets = (campaignId: string) =>
  apiRequest<IntakeFieldSetRow[]>('GET', `/api/campaigns/${campaignId}/intake-field-sets`);
export const upsertIntakeFieldSet = (campaignId: string, dto: UpsertIntakeFieldSetInput) =>
  apiRequest<IntakeFieldSetRow>('PUT', `/api/campaigns/${campaignId}/intake-field-sets`, dto);
export const deleteIntakeFieldSet = (campaignId: string, id: string) =>
  apiRequest<{ removed: boolean }>('DELETE', `/api/campaigns/${campaignId}/intake-field-sets/${id}`);

// ── S11 C1/C3 — field placement (ARCH §4.1, FROZEN) ─────────────────────────

export type SignatureField = {
  type: 'signature' | 'date' | 'name' | 'initials';
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
};

export type SignatureFieldsResult = {
  templateVersionId: string;
  signReady: boolean;
  fieldCount: number;
  docusealTemplateId: number | null;
  pageCount: number | null;
};

export const putSignatureFields = (templateVersionId: string, fields: SignatureField[]) =>
  apiRequest<SignatureFieldsResult>('PUT', `/api/templates/${templateVersionId}/signature-fields`, { fields });

// D-4 — human confirm gate (AD-4): re-reads fields from DocuSeal, overwrites
// signatureFields, sets fieldsConfirmed:true. Never called by an automated path.
export type ConfirmFieldsResult = {
  templateVersionId: string;
  fieldsConfirmed: true;
  fieldCount: number;
  signatureFields: SignatureField[];
};
export const confirmTemplateFields = (templateVersionId: string) =>
  apiRequest<ConfirmFieldsResult>('POST', `/api/templates/${templateVersionId}/confirm-fields`);

// rep-editor-20260727 (ARCH §1.1.e) — replaces admin field placement: a
// per-document requirement manifest (counts + required intake keys), no
// canvas. Always resolves (server defaults when unset) — the gate can never
// silently evaporate on an unconfigured template.
//
// SEV2 root-cause fix: the server (RequirementManifestService.get/put) responds
// `{ templateVersionId, manifest }`, never a flat RequirementManifest — a prior
// version of this file declared the flat shape as the return type, which was a
// type lie: the first caller that trusted it threw "Cannot read properties of
// undefined (reading 'join')" on data.requiredIntakeKeys. Unwrap here, once, so
// every caller keeps the flat RequirementManifest contract it already expects.
type RequirementManifestResponse = { templateVersionId: string; manifest: RequirementManifest };
export const getRequirementManifest = (templateVersionId: string) =>
  apiRequest<RequirementManifestResponse>('GET', `/api/templates/${templateVersionId}/requirement-manifest`).then(
    (res) => res.manifest,
  );
export const putRequirementManifest = (templateVersionId: string, dto: RequirementManifest) =>
  apiRequest<RequirementManifestResponse>('PUT', `/api/templates/${templateVersionId}/requirement-manifest`, dto).then(
    (res) => res.manifest,
  );

// ── Vendors ──────────────────────────────────────────────────────────────────

export type Vendor = {
  id: string;
  companyName: string;
  signerName: string;
  signerTitle: string;
  signerEmail: string;
  signerPhone: string;
  address: string;
  notes: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

/** GET /api/vendors row — Vendor plus the (scope-filtered) campaigns it has contracts on. */
// Optional so other list consumers/fixtures (contract pickers) need no change.
export type VendorListItem = Vendor & { campaigns?: { id: string; name: string }[] };

export type VendorInput = {
  companyName: string;
  signerName: string;
  signerTitle: string;
  signerEmail: string;
  signerPhone: string;
  address: string;
  notes?: string;
};

export type VendorListFilters = { includeArchived?: boolean; q?: string; dateFrom?: string; dateTo?: string };

export const listVendors = (filters: VendorListFilters = {}) => {
  const { includeArchived, ...rest } = filters;
  const qs = buildListQuery({ ...rest, includeArchived: includeArchived ? 'true' : undefined });
  return apiRequest<VendorListItem[]>('GET', `/api/vendors${qs}`);
};
// Same F5-class fix as getContract/getCampaign below — id is untrusted
// (straight from useParams), a raw '/' or a failed-decode raw '%' sequence
// must not turn into extra path segments or a server-side decode throw.
// Same slash guard as getCampaign above — see that comment.
export const getVendor = (id: string) =>
  id.includes('/')
    ? Promise.reject(new Error('Vendor not found'))
    : apiRequest<Vendor>('GET', `/api/vendors/${encodeURIComponent(id)}`);
export const createVendor = (dto: VendorInput) => apiRequest<Vendor>('POST', '/api/vendors', dto);
export const updateVendor = (id: string, dto: Partial<VendorInput>) =>
  apiRequest<Vendor>('PATCH', `/api/vendors/${id}`, dto);
export const archiveVendor = (id: string) => apiRequest<Vendor>('POST', `/api/vendors/${id}/archive`);
// R28 archive-visibility close — undoes archiveVendor; same response shape.
export const restoreVendor = (id: string) => apiRequest<Vendor>('POST', `/api/vendors/${id}/restore`);

// ── Contracts ────────────────────────────────────────────────────────────────

export type ContractStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'SENT'
  | 'SIGNED'
  | 'ONBOARDING_SENT'
  | 'REJECTED'
  | 'VOIDED';

export type Contract = {
  id: string;
  vendorId: string;
  campaignId: string;
  effectiveDate: string;
  compValues: Record<string, string>;
  status: ContractStatus;
  source: 'ESIGNED' | 'MANUAL_UPLOAD';
  templateVersionId: string | null;
  generatedDocxId: string | null;
  generatedPdfId: string | null;
  signedPdfId: string | null;
  vendorSignToken: string | null;
  compException1: string | null;
  compException2: string | null;
  compException3: string | null;
  reviewNote?: string | null; // reject reason (D19) — optional so existing fixture literals elsewhere don't all need updating; read via the REJECTED ContractEvent payload in the timeline, not this field
  // S32/D38: opt-in flag for the 5PM CST scheduled onboarder export email.
  includeInDailyExport: boolean;
  // S37/D43: opt-in flag for the per-channel team-lead export email.
  includeInTeamLeadExport: boolean;
  // S37/D43: shared scheduled-export frequency, governs BOTH the daily
  // roster export and the team-lead export for this contract.
  dailyExportFrequency: 'DAILY_5PM' | 'HOURLY';
  // S38/D51: last resolved "sent to" address for this contract's export, and
  // the per-contract TO/CC override for "Send export now".
  sentToEmail: string | null;
  exportRecipientEmail: string | null;
  exportCcEmails: string[];
  createdAt: string;
  updatedAt: string;
};

export type ContractEvent = {
  id: string;
  contractId: string;
  type: string;
  actorUserId: string | null;
  actor: { name: string } | null;
  payload: unknown;
  createdAt: string;
};

export type ContractWithRelations = Contract & {
  vendor: Vendor;
  campaign: Campaign;
  events: ContractEvent[];
  onboardingLinks: OnboardingLink[];
};

export type ContractInput = {
  vendorId: string;
  campaignId: string;
  effectiveDate: string;
  compValues: Record<string, string>;
  compException1?: string;
  compException2?: string;
  compException3?: string;
};

export type ContractListFilters = {
  status?: ContractStatus;
  campaignId?: string;
  q?: string;
  dateFrom?: string;
  dateTo?: string;
};

export const listContracts = (filters: ContractListFilters = {}) =>
  apiRequest<Contract[]>('GET', `/api/contracts${buildListQuery(filters)}`);
// Same F5-class fix as getCampaign above — see that comment. This is the
// exact call ContractDetailPage makes from the :id route param.
// Same slash guard as getCampaign above — see that comment.
export const getContract = (id: string) =>
  id.includes('/')
    ? Promise.reject(new Error('Contract not found'))
    : apiRequest<ContractWithRelations>('GET', `/api/contracts/${encodeURIComponent(id)}`);
export const createContract = (dto: ContractInput) => apiRequest<Contract>('POST', '/api/contracts', dto);
export const generateContract = (id: string) => apiRequest<Contract>('POST', `/api/contracts/${id}/generate`);
export const submitContract = (id: string) => apiRequest<Contract>('POST', `/api/contracts/${id}/submit`);

export const fetchContractPreviewPdf = (id: string) => apiGetBlob(`/api/contracts/${id}/preview`);

// T3 (ARCH §4.5) — mirrors fetchContractPreviewPdf, distinct route segment (L4).
// 404 CONTRACT_NOT_FOUND / 409 NO_SIGNED_PDF surface as ApiError via apiGetBlob.
export const fetchContractSignedPdf = (id: string) => apiGetBlob(`/api/contracts/${id}/signed-pdf`);

export const voidContract = (id: string) => apiRequest<Contract>('POST', `/api/contracts/${id}/void`);

// S32/D38: per-contract opt-in for the 5PM CST scheduled onboarder export email.
export const setContractDailyExport = (id: string, includeInDailyExport: boolean) =>
  apiRequest<Contract>('PATCH', `/api/contracts/${id}/daily-export`, { includeInDailyExport });

// S37/D43: per-contract opt-in for the per-channel team-lead export email.
export const setContractTeamLeadExport = (id: string, includeInTeamLeadExport: boolean) =>
  apiRequest<Contract>('PATCH', `/api/contracts/${id}/team-lead-export`, { includeInTeamLeadExport });

// S37/D43: shared scheduled-export frequency (governs BOTH the daily roster
// export and the team-lead export) for this contract.
export const setContractExportFrequency = (id: string, dailyExportFrequency: 'DAILY_5PM' | 'HOURLY') =>
  apiRequest<Contract>('PATCH', `/api/contracts/${id}/export-frequency`, { dailyExportFrequency });

export type ContractExportRecipientsInput = {
  exportRecipientEmail?: string | null;
  exportCcEmails?: string[];
};

export function setContractExportRecipients(id: string, input: ContractExportRecipientsInput) {
  return apiRequest<Contract>('PATCH', `/api/contracts/${id}/export-recipients`, input);
}

// Admin-only immediate "Send now" export (D58, revised D59) — fires
// WHICHEVER reports this contract is opted in to: the roster export (same
// "send now even if not opted in" affordance as before — see the API's
// send-export-now route comment for the exact roster-skip rule) and, when
// includeInTeamLeadExport is true, ALSO the team-lead export. `delivered:false`
// (with `reason`) means the roster wasn't sent, not an error. `teamLead` is
// present only when includeInTeamLeadExport is true for this contract;
// `teamLead.sent` false (with `reason`) means the team-lead send was skipped
// (e.g. no onboarders), not an error. D59: the team-lead export is now ONE
// email per contract (Rob Morris asked for this — previously it was one
// email per channel/team), so `teamLead.channelsSent` is gone; `rowCount` (the
// number of onboarder rows in that one workbook) replaces it.
export const sendContractExportNow = (id: string) =>
  apiRequest<{
    delivered: boolean;
    recipientCount: number;
    reason?: string;
    teamLead?: { sent: boolean; rowCount: number; reason?: string };
  }>('POST', `/api/contracts/${id}/send-export-now`);

// ── Wave 3: approvals, public signing, push notifications ──────────────────

export const approveContract = (contractId: string) =>
  apiRequest<Contract>('POST', `/api/approvals/${contractId}/approve`);
export const rejectContract = (contractId: string, note: string) =>
  apiRequest<Contract>('POST', `/api/approvals/${contractId}/reject`, { note });

// A2 (2026-07-27): countersigning removed — vendor is the only signer now.
export type SignTokenResponse = { role: 'vendor'; src: string };

/**
 * GET /api/sign/:token — @Public(), no auth header, no refresh/redirect.
 * Plain fetch (not apiRequest): apiRequest's 401-retry path calls
 * redirectToLogin() on failure, which would bounce a signed-out vendor off
 * this public link to /login. 404 -> ApiError code SIGN_TOKEN_NOT_FOUND.
 */
export async function getSignToken(token: string): Promise<SignTokenResponse> {
  const res = await safeFetch(`${API_BASE}/api/sign/${token}`);
  if (!res.ok) throw await parseApiError(res);
  return res.json() as Promise<SignTokenResponse>;
}

export type PushSubscribeInput = { endpoint: string; p256dh: string; auth: string; userAgent?: string };

export const getVapidPublicKey = () => apiRequest<{ publicKey: string | null }>('GET', '/api/notifications/vapid-public-key');
export const subscribePush = (dto: PushSubscribeInput) =>
  apiRequest<{ id: string; endpoint: string }>('POST', '/api/notifications/subscribe', dto);
export const unsubscribePush = (id: string) =>
  apiRequest<{ removed: boolean }>('DELETE', `/api/notifications/subscribe/${id}`);

// ── Wave 4: rep onboarding links + public onboarding + manual upload ───────

export type OnboardingLink = {
  id: string;
  contractId: string;
  state: string;
  // ARCH §4.5 (S11 B7) — free-form, null when omitted at creation.
  channel: string | null;
  slug: string;
  active: boolean;
  createdAt: string;
  deactivatedAt: string | null;
};

export type CreateOnboardingLinkInput = { state: string; channel?: string };

// ARCH §4.5 (S11 B7) — 409 LINK_EXISTS when contractId+state+channel already has a link.
export const createOnboardingLink = (contractId: string, dto: CreateOnboardingLinkInput) =>
  apiRequest<OnboardingLink>('POST', `/api/onboarding-links/contract/${contractId}`, dto);

// Kind→name is frozen server-side (ARCH §4.5) — FE just renders `name` verbatim, never hardcodes it.
export type OnboardDocument = { kind: string; name: string; signReady: boolean };
export type OnboardContext = {
  campaignName: string;
  product: string;
  state: string;
  // ARCH §4.3/§4.5 (S11 C4 gate) — AND of every document's signReady.
  ready: boolean;
  documents: OnboardDocument[];
  // W-A: every rep-facing input, including identity (name/email/phone/DOB/
  // office/manager) — no more separate hardcoded trio. [] only if a campaign
  // has no fields configured at all.
  fields: IntakeField[];
};
// W-A — the server derives repName/repEmail/repPhone from fieldValues; the
// client no longer collects or sends them as separate top-level properties
// (they used to duplicate campaign-defined fields of the same purpose).
export type OnboardIntakeInput = {
  // S12 §A5/§C5 — answers to `fields` above, keyed by IntakeField.key.
  fieldValues: Record<string, string | number | boolean>;
  // S46 (contract §8) — optional SMS-consent checkbox; omitted/false stores
  // nothing, true stamps smsConsentAt + smsConsentVersion server-side.
  smsConsent?: boolean;
};

// rep-editor-20260727 (ARCH §3.3) — DocuSeal removed from the rep path.
// POST /api/onboard/:slug now returns the prepared editor session instead of
// a hosted-signing `{ src }`; step 2 is the in-browser editor, not a redirect.
export type StartedRepSubmission = { submissionId: string; editorToken: string };

/**
 * GET /api/onboard/:slug — @Public(), no auth header, no refresh/redirect.
 * Same reasoning as getSignToken: apiRequest's redirect-on-401 path would
 * bounce a signed-out rep off this public link to /login. 404 -> ApiError
 * code ONBOARDING_LINK_NOT_FOUND.
 */
export async function getOnboardContext(slug: string): Promise<OnboardContext> {
  const res = await safeFetch(`${API_BASE}/api/onboard/${slug}`);
  if (!res.ok) throw await parseApiError(res);
  return res.json() as Promise<OnboardContext>;
}

/** POST /api/onboard/:slug — @Public(), same plain-fetch contract as getOnboardContext. */
export async function submitOnboardIntake(slug: string, dto: OnboardIntakeInput): Promise<StartedRepSubmission> {
  const res = await safeFetch(`${API_BASE}/api/onboard/${slug}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json() as Promise<StartedRepSubmission>;
}

// ── rep-editor-20260727 W1 — the rep-side editor (ARCH §3.2-3.4) ───────────
// All @Public(), plain-fetch contract (same reasoning as getOnboardContext:
// apiRequest's 401-redirect path doesn't belong on a signed-out-rep page).

import type { EditorContext, RepCompleteDto, RepCompleteResult, RequirementManifest } from '../features/onboarding/editor/editor-types';
export type {
  EditorContext,
  RepCompleteDto,
  RepCompleteResult,
  RepPlacement,
  RequirementManifest,
  SubmissionIncompleteBody,
} from '../features/onboarding/editor/editor-types';

export async function getEditorContext(slug: string, editorToken: string): Promise<EditorContext> {
  const res = await safeFetch(`${API_BASE}/api/onboard/${slug}/editor/${editorToken}`);
  if (!res.ok) throw await parseApiError(res);
  return res.json() as Promise<EditorContext>;
}

/** Resolves a server-relative path (e.g. EditorContext.documents[].pdfUrl) against the configured API base — for pdfjs's getDocument({ url }) and signed-PDF links, which fetch directly rather than through apiRequest. */
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

export async function completeEditorSubmission(
  slug: string,
  editorToken: string,
  dto: RepCompleteDto,
): Promise<RepCompleteResult> {
  const res = await safeFetch(`${API_BASE}/api/onboard/${slug}/editor/${editorToken}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json() as Promise<RepCompleteResult>;
}

export const deactivateOnboardingLink = (id: string) =>
  apiRequest<OnboardingLink>('POST', `/api/onboarding-links/${id}/deactivate`);

// ARCH §4.3 (R2 DECISION): rotates the link's slug in place. Response is the
// same OnboardingLink shape with the new slug/active/deactivatedAt.
export const regenerateOnboardingLink = (id: string) =>
  apiRequest<OnboardingLink>('POST', `/api/onboarding-links/${id}/regenerate`);

export type LinksEmailResult = { to: string; count: number; delivered: boolean; reason?: string };
export const resendOnboardingLinks = (contractId: string) =>
  apiRequest<LinksEmailResult>('POST', `/api/onboarding-links/contract/${contractId}/resend`);

// T2 — mirrors apps/api/src/onboarding/links.controller.ts:25-33 verbatim (frozen,
// ARCH §4.3), except createdAt: string (JSON wire format) vs the backend's Date —
// the one permitted divergence, a serialization fact not a design choice.
export type AttributedSubmission = {
  id: string;
  repName: string;
  repEmail: string;
  repPhone: string;
  status: string;
  createdAt: string;
  onboardingLinkId: string;
  link: { state: string; contractId: string };
  // S12 §C5 — composed {key,label,value} answers for this submission's intake fields.
  fieldAnswers: Array<{ key: string; label: string; value: string }>;
  // declared-fields-20260731 — mirrors links.controller.ts's AttributedSubmission
  // verbatim (uploads: normalizeUploads(s.uploads)): which of dl/headshot/optional
  // this submission actually has, so a view action only renders for a slot with a file.
  uploads: Uploads;
  // S22 (B4) — EGC-staff back-office overlay, read-only here. {} when never touched.
  adminFieldValues: OnboarderFieldValues;
};

export const fetchContractSubmissions = (contractId: string) =>
  apiRequest<AttributedSubmission[]>('GET', `/api/onboarding-links/contract/${contractId}/submissions`);

// D-5 — the secure in-portal replacement for emailing a rep's signed packet as
// an attachment. Admin-only; 404 SUBMISSION_NOT_FOUND / DOCUMENT_NOT_AVAILABLE
// (STARTED / no PDF yet) surface as ApiError via apiGetBlob.
export const fetchSubmissionSignedPdf = (submissionId: string) =>
  apiGetBlob(`/api/onboarding-links/submissions/${submissionId}/document`);

// declared-fields-20260731 — admin view of a rep's uploaded DL/headshot/optional
// bytes (links.controller.ts's getUpload). Same apiGetBlob auth/401-retry
// contract as fetchSubmissionSignedPdf above; 404 SUBMISSION_NOT_FOUND /
// UPLOAD_NOT_AVAILABLE surface as ApiError.
export const fetchSubmissionUpload = (submissionId: string, slot: UploadSlotKey) =>
  apiGetBlob(`/api/onboarding-links/submissions/${submissionId}/uploads/${slot}`);

/** Manual bypass upload (already-signed paper/PDF) — multipart, field name 'file'. Assumed to return the updated Contract, mirroring generate/submit/void. */
export function manualUploadContract(contractId: string, file: File): Promise<Contract> {
  const form = new FormData();
  form.append('file', file);
  return apiPostForm(`/api/contracts/${contractId}/manual-upload`, form);
}

// ── S1 — wet-signature contract upload shortcut (ARCH §4, FROZEN) ──────────
// One multipart endpoint: creates/reuses the vendor + contract in a single
// transaction, guarded by full campaign readiness BEFORE any write (409
// CAMPAIGN_NOT_READY, zero rows written), then skips the approval queue
// straight to SIGNED and fires the existing onboarding-link pipeline. No
// compValues field exists on this request — the server persists {}.

export type WetUploadVendorInput =
  | { vendorId: string }
  | {
      companyName: string;
      signerName: string;
      signerTitle: string;
      signerEmail: string;
      signerPhone: string;
      address: string;
      notes?: string;
    };

export type WetUploadResult = {
  contract: {
    id: string;
    vendorId: string;
    campaignId: string;
    status: string;
    source: string;
    effectiveDate: string;
    createdAt: string;
  };
  vendor: { id: string; companyName: string; signerEmail: string };
  vendorCreated: boolean;
  onboardingLinks: { state: string; channel: string | null; url: string }[];
};

/** POST /api/contracts/wet-upload — the discriminated WetUploadVendorInput
 * union makes "both vendorId and new-vendor fields" unrepresentable on the
 * client; the server still checks it (S1-7, 400 VENDOR_INPUT_AMBIGUOUS) — a
 * union is a compile-time comfort, not a trust boundary. 409
 * CAMPAIGN_NOT_READY carries `body.blockers`; callers branch on `err.code`,
 * never on `err.message`. */
export function wetUploadContract(input: {
  file: File;
  campaignId: string;
  effectiveDate: string;
  vendor: WetUploadVendorInput;
}): Promise<WetUploadResult> {
  const form = new FormData();
  form.append('file', input.file);
  form.append('campaignId', input.campaignId);
  form.append('effectiveDate', input.effectiveDate);
  if ('vendorId' in input.vendor) {
    form.append('vendorId', input.vendor.vendorId);
  } else {
    form.append('companyName', input.vendor.companyName);
    form.append('signerName', input.vendor.signerName);
    form.append('signerTitle', input.vendor.signerTitle);
    form.append('signerEmail', input.vendor.signerEmail);
    form.append('signerPhone', input.vendor.signerPhone);
    form.append('address', input.vendor.address);
    if (input.vendor.notes) form.append('notes', input.vendor.notes);
  }
  return apiPostForm(`/api/contracts/wet-upload`, form);
}

// ── Wave 6: user management ─────────────────────────────────────────────────

export type ManagedUser = {
  id: string;
  email: string;
  name: string;
  roles: Role[];
  mustChangePassword: boolean;
  deactivatedAt: string | null;
  createdAt: string;
  // S39/D95: campaigns this Staff user is scoped to. Empty for non-Staff roles.
  campaignAssignments: { campaignId: string; campaignName: string }[];
};

export type ManagedUserInput = { email: string; name: string; roles?: Role[] };

const USER_ERROR_MESSAGES: Record<string, string> = {
  EMAIL_TAKEN: 'That email is already in use',
  LAST_ACTIVE_USER: "You can't deactivate the last active user",
  CANNOT_DEACTIVATE_SELF: "You can't deactivate your own account",
};

/** Rewrites the known 409 codes above to friendly copy; any other ApiError passes through untouched. */
async function withFriendlyUserErrors<T>(p: Promise<T>): Promise<T> {
  try {
    return await p;
  } catch (err) {
    const friendly = err instanceof ApiError && err.code ? USER_ERROR_MESSAGES[err.code] : undefined;
    if (err instanceof ApiError && friendly) {
      throw new ApiError(friendly, err.code, err.body);
    }
    throw err;
  }
}

export const listUsers = () => apiRequest<ManagedUser[]>('GET', '/api/users');
export const createUser = (dto: ManagedUserInput) =>
  withFriendlyUserErrors(apiRequest<{ user: ManagedUser; tempPassword: string }>('POST', '/api/users', dto));
export const updateUser = (id: string, dto: Partial<ManagedUserInput>) =>
  withFriendlyUserErrors(apiRequest<ManagedUser>('PATCH', `/api/users/${id}`, dto));
export const resetUserPassword = (id: string) =>
  apiRequest<{ tempPassword: string }>('POST', `/api/users/${id}/reset-password`);
export const deactivateUser = (id: string) =>
  withFriendlyUserErrors(apiRequest<ManagedUser>('POST', `/api/users/${id}/deactivate`));
export const reactivateUser = (id: string) => apiRequest<ManagedUser>('POST', `/api/users/${id}/reactivate`);

// S39/D95: Staff role is campaign-scoped. setUserRoles replaces the user's
// role set; setUserCampaignAssignments replaces the campaign-assignment set
// atomically and returns the new list.
export const setUserRoles = (id: string, roles: Role[]) =>
  withFriendlyUserErrors(apiRequest<ManagedUser>('PATCH', `/api/users/${id}/roles`, { roles }));

export const setUserCampaignAssignments = (id: string, campaignIds: string[]) =>
  withFriendlyUserErrors(
    apiRequest<{ campaignId: string; campaignName: string }[]>(
      'PUT',
      `/api/users/${id}/campaign-assignments`,
      { campaignIds },
    ),
  );

// S41: Admin API-keys page
// Vendor scoping (2026-09-24): partner keys are now scoped by VENDOR, not
// campaign (see apps/api DECISIONS.md). `campaigns`/`campaignIds`/
// setPartnerApiKeyCampaigns are KEPT for back-compat/visibility into legacy
// grants only — they no longer affect what a key can read. `vendors`/
// `vendorIds`/setPartnerApiKeyVendors are the auth-effective grant.
export type PartnerApiKeyCampaignRef = { id: string; name: string };
export type PartnerApiKeyVendorRef = { id: string; companyName: string };
export type PartnerApiKey = {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  campaigns: PartnerApiKeyCampaignRef[];
  vendors: PartnerApiKeyVendorRef[];
};
export type CreatePartnerApiKeyInput = { name: string; vendorIds?: string[]; campaignIds?: string[] };
export type CreatePartnerApiKeyResult = { key: PartnerApiKey; rawKey: string };
export type PartnerApiKeyVendorOptionContract = { id: string; campaignName: string; status: string };
export type PartnerApiKeyVendorOption = { id: string; companyName: string; contracts: PartnerApiKeyVendorOptionContract[] };

export const listPartnerApiKeys = () => apiRequest<PartnerApiKey[]>('GET', '/api/admin/partner-api-keys');
export const createPartnerApiKey = (dto: CreatePartnerApiKeyInput) => apiRequest<CreatePartnerApiKeyResult>('POST', '/api/admin/partner-api-keys', dto);
export const setPartnerApiKeyCampaigns = (id: string, campaignIds: string[]) => apiRequest<PartnerApiKey>('PUT', `/api/admin/partner-api-keys/${encodeURIComponent(id)}/campaigns`, { campaignIds });
export const setPartnerApiKeyVendors = (id: string, vendorIds: string[]) => apiRequest<PartnerApiKey>('PUT', `/api/admin/partner-api-keys/${encodeURIComponent(id)}/vendors`, { vendorIds });
export const revokePartnerApiKey = (id: string) => apiRequest<PartnerApiKey>('POST', `/api/admin/partner-api-keys/${encodeURIComponent(id)}/revoke`);
// q is trimmed server-side; pass through as typed (empty string omitted).
export const listPartnerApiKeyVendorOptions = (q?: string) => {
  const qs = q?.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
  return apiRequest<PartnerApiKeyVendorOption[]>('GET', `/api/admin/partner-api-keys/vendor-options${qs}`);
};

// ── S43: Help assistant ──────────────────────────────────────────────────────

export type AssistantErrorCode =
  | 'ASSISTANT_UNAVAILABLE'
  | 'ASSISTANT_TIMEOUT'
  | 'ASSISTANT_RATE_LIMITED'
  | 'ASSISTANT_REFUSED'
  | 'ASSISTANT_EMPTY'
  | 'ASSISTANT_UPSTREAM_ERROR';
export type AssistantStatus = { configured: boolean; model: string };
export type AskAssistantResult = { answer: string; model: string } | { error: { code: AssistantErrorCode; message: string } };

export const getAssistantStatus = () => apiRequest<AssistantStatus>('GET', '/api/assistant/status');
export const askAssistant = (question: string) => apiRequest<AskAssistantResult>('POST', '/api/assistant/ask', { question });

// ── Rep-facing help assistant (S47) — public, no auth ───────────────────────
// GET/POST /api/rep-assistant/:slug/* are @Public(), same plain-fetch
// contract as getOnboardContext: no auth header, no 401 refresh/redirect,
// since a signed-out rep on /onboard/:slug has no session to redirect from.
// Errors carry `code`: REP_ASSISTANT_DISABLED (503), REP_ASSISTANT_DAILY_LIMIT
// (429), REP_ASSISTANT_UNAVAILABLE (502), plus a plain 404 when the link
// itself doesn't resolve.

export type RepAssistantStatus = { enabled: boolean; contactEmail: string | null };
export type RepAssistantAskResult = { answer: string; remainingToday: number };

export async function getRepAssistantStatus(slug: string): Promise<RepAssistantStatus> {
  const res = await safeFetch(`${API_BASE}/api/rep-assistant/${slug}/status`);
  if (!res.ok) throw await parseApiError(res);
  return res.json() as Promise<RepAssistantStatus>;
}

export async function askRepAssistant(slug: string, question: string): Promise<RepAssistantAskResult> {
  const res = await safeFetch(`${API_BASE}/api/rep-assistant/${slug}/ask`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ question }),
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json() as Promise<RepAssistantAskResult>;
}

// ── Settings — email (SMTP) ─────────────────────────────────────────────────

export type TestEmailResult = { delivered: boolean; reason?: string };

export const sendTestEmail = (to: string) =>
  apiRequest<TestEmailResult>('POST', '/api/settings/email/test', { to });

// ── Settings — badges (D76 standing CC) ─────────────────────────────────────
// GET/PUT /api/settings/badges. Rob Morris (after generating a real Wave
// badge): "sent to contact@egc-inc... or whatever email we put here" — this
// is the ONE standing CC every generated badge is automatically copied to
// (settings.service.ts's BADGE_STANDING_CC_DEFAULT = contact@egc-inc.com
// when unset), separate from D75's per-badge typed `cc` in issueBadge above
// (a one-off, typed at send time, not a stored setting). The wire field name
// is `standingCc` (matches UpdateBadgeSettingsDto exactly) — not
// `standingCcEmail`, despite the JSON storage key on Campaign being named
// that; this type mirrors the HTTP contract, not the DB column.
export type BadgeStandingCcSettings = { standingCc: string };

export const getBadgeStandingCc = () => apiRequest<BadgeStandingCcSettings>('GET', '/api/settings/badges');

export const updateBadgeStandingCc = (standingCc: string) =>
  apiRequest<BadgeStandingCcSettings>('PUT', '/api/settings/badges', { standingCc });

// ── Resources — central file library ────────────────────────────────────────

// S26 — a Resource is file XOR link (see resources.service.ts): `type` is the
// server-derived discriminator, `document` is null and `url` is set for a link
// resource, and vice versa for a file resource.
export type Resource = {
  id: string;
  displayName: string;
  filename: string;
  category: string | null;
  archivedAt: string | null;
  createdAt: string;
  uploadedBy: string;
  type: 'file' | 'link';
  document: { mimeType: string; sizeBytes: number; sha256: string } | null;
  url: string | null;
  folderId: string | null;
};

export type ResourceListFilters = { category?: string; includeArchived?: boolean };

export const listResources = (filters: ResourceListFilters = {}) => {
  const { includeArchived, ...rest } = filters;
  const qs = buildListQuery({ ...rest, includeArchived: includeArchived ? 'true' : undefined });
  return apiRequest<Resource[]>('GET', `/api/resources${qs}`);
};

export const uploadResource = (form: FormData) => apiPostForm<Resource>('/api/resources', form);

// S26 — creates a link resource (a plain https URL, no file behind it). Mirrors
// CreateLinkResourceDto server-side: url required, displayName/category optional.
export type CreateLinkResourceInput = { url: string; displayName?: string; category?: string; folderId?: string };
export const createLinkResource = (dto: CreateLinkResourceInput) =>
  apiRequest<Resource>('POST', '/api/resources/link', dto);

export const downloadResource = (id: string) => apiGetBlob(`/api/resources/${id}/file`);

// ── Resource folders — restrict a folder of resources to specific users ────
// Field names below are copied from the real backend
// (apps/api/src/resources/resource-folders.service.ts's ResourceFolderListItem),
// not guessed: `access[]` entries carry the ResourceFolderAccess row's own id
// plus the granted user's email/name — there is no userId on the wire, so the
// UI matches an access entry back to a ManagedUser by email (see FolderDialog).

/** One entry on a restricted folder's access list — `id` is the access ROW's id, not the user's id. */
export type ResourceFolderAccess = { id: string; email: string; name: string };

export type ResourceFolder = {
  id: string;
  name: string;
  restricted: boolean;
  resourceCount: number;
  // Always an array from the server (empty for an open folder) — never absent.
  access: ResourceFolderAccess[];
};

export type CreateFolderInput = { name: string; restricted?: boolean; userIds?: string[] };
export const createFolder = (dto: CreateFolderInput) =>
  apiRequest<ResourceFolder>('POST', '/api/resources/folders', dto);

/** Folders visible to the current user (server filters restricted ones); always excludes archived — no includeArchived param exists server-side. */
export const listFolders = () => apiRequest<ResourceFolder[]>('GET', '/api/resources/folders');

export type UpdateFolderInput = { name?: string; restricted?: boolean; userIds?: string[] };
export const updateFolder = (id: string, dto: UpdateFolderInput) =>
  apiRequest<ResourceFolder>('PATCH', `/api/resources/folders/${id}`, dto);

// archive/restore return the bare Prisma ResourceFolder row (no resourceCount/access,
// unlike list/create/update's ResourceFolderListItem) — callers refetch listFolders()
// for the up-to-date shape rather than using this response directly.
type ArchivedResourceFolder = { id: string; name: string; restricted: boolean; createdBy: string; archivedAt: string | null; createdAt: string };
export const archiveFolder = (id: string) => apiRequest<ArchivedResourceFolder>('POST', `/api/resources/folders/${id}/archive`);
export const restoreFolder = (id: string) => apiRequest<ArchivedResourceFolder>('POST', `/api/resources/folders/${id}/restore`);

/** Admin-only onboarder export for one contract — channel-tabbed .xlsx. 403 for non-Admin, 404 CONTRACT_NOT_FOUND. */
export const downloadContractOnboardersExport = (contractId: string): Promise<Blob> =>
  apiGetBlob(`/api/exports/contracts/${contractId}/onboarders`);

// D81 (Rob Morris, 2026-09-15): "export by campaign only instead of have to
// always take everyone for every campaign." Optional campaignId narrows the
// SAME /api/exports/onboarders route (no new endpoint) to one campaign;
// omitting it reproduces the pre-D81 unfiltered export exactly.
/** Admin-only portfolio-wide (or, with campaignId, single-campaign) onboarder export — single-sheet .xlsx. */
// D147: optional certUploadedFrom/To (YYYY-MM-DD, Central) narrow the export to the
// same "Certificate uploaded" window the Onboarders list uses; omitted = unfiltered.
// D150 (S54): optional certSent narrows by the "Certificate sent" filter — same
// 'NOT_SENT' | 'SENT' values as the Onboarders list, omitted = Any.
export const downloadAllOnboardersExport = (
  campaignId?: string,
  cert?: { certUploadedFrom?: string; certUploadedTo?: string; certSent?: CertSentFilter },
): Promise<Blob> =>
  apiGetBlob(
    `/api/exports/onboarders${buildListQuery({
      campaignId,
      certUploadedFrom: cert?.certUploadedFrom,
      certUploadedTo: cert?.certUploadedTo,
      certSent: cert?.certSent,
    })}`,
  );

// D66: one row per active onboarding link (campaign/vendor/state/channel/link).
export const downloadOnboardingLinksExport = (): Promise<Blob> =>
  apiGetBlob('/api/exports/onboarding-links');

// D71/D77 (unverified is final): the Wave master roster filtered to rows
// whose email is NOT verified — the action list of people to chase about
// confirming their email. See ExportsService.exportUnverifiedWaveEmails's
// header comment for the full renamed-twice history.
export const downloadWaveUnverifiedEmailsExport = (): Promise<Blob> =>
  apiGetBlob('/api/exports/wave-unverified-emails');

// D8x (Rob Morris, SMS 2026-09-17): "Nationwide Spark" / "Pull P2 Spark" —
// reps on one specific campaign whose "Sent to Client" status shows them
// awaiting a Spark test. campaignKey is the literal route segment the API
// expects ('p2' = Spark Socal Gas P2, 'nationwide' = Spark Southern
// California) — NOT a campaign id, so this intentionally does not go through
// buildListQuery/listCampaigns like the other campaign-scoped export above.
export type SparkTestRequestCampaignKey = 'p2' | 'nationwide';

export const downloadSparkTestRequestsExport = (campaignKey: SparkTestRequestCampaignKey): Promise<Blob> =>
  apiGetBlob(`/api/exports/spark-test-requests/${campaignKey}`);

export const archiveResource = (id: string) => apiRequest<Resource>('POST', `/api/resources/${id}/archive`);
// R28 archive-visibility close — undoes archiveResource; same response shape.
export const restoreResource = (id: string) => apiRequest<Resource>('POST', `/api/resources/${id}/restore`);

export const createTemplateFromResource = (
  campaignId: string,
  dto: { resourceId: string; kind?: string; state?: string; channel?: string },
) =>
  apiRequest<{ templateVersion: TemplateVersion; validation: ValidationReport }>(
    'POST',
    `/api/campaigns/${campaignId}/templates/from-resource`,
    dto,
  );

// ── S18 — contract coordinate fields (ARCH §3 frozen wire contract, copied
// verbatim — client/server type drift on this exact seam has caused four
// production bugs). apps/web/src/lib/contract-fields.ts is the hand-kept
// mirror of the API-side ContractField type; edit BOTH together. ──

export type { ContractField, ContractFieldSource } from './contract-fields';

export type PreparationQueueItem = {
  templateVersionId: string;
  campaignId: string;
  campaignName: string;
  version: number;
  state: string | null;
  channel: string | null;
  uploadedByName: string | null;
  createdAt: string;
  hasFields: boolean;
  supersededByVersion: number | null;
};

export const getPreparationQueue = () => apiRequest<PreparationQueueItem[]>('GET', '/api/templates/preparation-queue');

export type ContractFieldsGates = {
  compRows: { placedPairs: number; requiredPairs: number; missingLabels: string[] };
  missingSources: string[]; // human labels of unplaced REQUIRED_CONTRACT_SOURCES
  previewedCurrent: boolean; // stored fingerprint === recomputed fingerprint
};

export type ContractFieldsView = {
  templateVersionId: string;
  campaignId: string;
  campaignName: string;
  version: number;
  status: TemplatePreparationStatus;
  supersededByVersion: number | null;
  pageCount: number;
  compLabels: string[]; // the campaign's, in order — drives the palette
  contractFields: ContractField[]; // null column -> []
  gates: ContractFieldsGates;
};

export const getContractFields = (templateVersionId: string) =>
  apiRequest<ContractFieldsView>('GET', `/api/templates/${templateVersionId}/contract-fields`);
export const putContractFields = (templateVersionId: string, contractFields: ContractField[]) =>
  apiRequest<ContractFieldsView>('PUT', `/api/templates/${templateVersionId}/contract-fields`, { contractFields });
export const previewContractFields = (templateVersionId: string) =>
  apiPostBlob(`/api/templates/${templateVersionId}/contract-fields/preview`);
export const publishContractFields = (templateVersionId: string) =>
  apiRequest<ContractFieldsView>('POST', `/api/templates/${templateVersionId}/contract-fields/publish`);

// ── S22 — Onboarder directory + admin fields (ARCH §2.5, F1) ─────────────────
// apiRequest already accepts 'PUT' (see putContractFields above) — no change
// needed to the request plumbing itself.

/** Intake types MINUS 'ssn' (ARCH §0 C4 — ssn is structurally disallowed here), PLUS
 * 'file' (S31/D37) — a portal-only attached file, value managed only by the
 * upload/clear routes (see OnboarderAdminFieldsCell), never entered as text. */
export type OnboarderFieldType = 'text' | 'email' | 'phone' | 'number' | 'date' | 'select' | 'checkbox' | 'file';

/** No `required` — ARCH §0 C5. */
export type OnboarderField = { key: string; label: string; type: OnboarderFieldType; options?: string[] };

export type OnboarderFieldValues = Record<string, string | number | boolean>;

export type OnboarderFieldSetResponse = { fields: OnboarderField[]; updatedAt: string | null };

// S45 background guardrail (docs/contracts/s45-background-guardrail.md
// "Shared types") — mirrors BackgroundCheckStatus/BackgroundCheckSummary in
// apps/api/src/background-review/background-check.ts exactly. `status: null`
// is "Pending" (no Veritas report received yet), never a fifth string value.
// S46 (contract §§2, 5.4) — document-check verdicts, reason codes and the
// per-slot summary shape the Onboarders list and review dialog read.
export type DocCheckStatus = 'PENDING_CHECK' | 'VERIFIED' | 'REJECTED' | 'NEEDS_REVIEW';

// contract §5.4 REASON_COPY keys, verbatim.
export type DocCheckReasonCode =
  | 'WRONG_DOCUMENT'
  | 'UNREADABLE'
  | 'EXPIRED'
  | 'NAME_MISMATCH'
  | 'DL_NO_PHOTO'
  | 'NO_FACE'
  | 'MULTIPLE_FACES'
  | 'FACE_NOT_CLEAR'
  | 'UNPROFESSIONAL'
  | 'OTHER'
  | 'EXPIRY_NOT_FOUND'
  | 'LOW_CONFIDENCE'
  | 'AI_INCONCLUSIVE'
  | 'AI_DISABLED'
  | 'AI_UNAVAILABLE'
  | 'UNSUPPORTED_FOR_AUTO_CHECK'
  | 'CHECK_INTERRUPTED';

// contract §2 DocCheckSummary.
export type DocCheckSummary = {
  status: DocCheckStatus;
  reasonCode: DocCheckReasonCode | null;
  reasonText: string | null;
  reviewerNote: string | null;
  checkedBy: 'AI' | 'USER' | 'SYSTEM';
  checkedAt: string;
  reviewedByName: string | null;
  documentId: string;
};

export type DocChecksView = { dl: DocCheckSummary | null; headshot: DocCheckSummary | null };

export type BackgroundCheckStatus = 'CLEAN' | 'HELD' | 'CLEARED' | 'DECLINED';

export type BackgroundCheckSummary = {
  status: BackgroundCheckStatus | null;
  reviewedAt: string | null;
  reviewedByName: string | null;
  declineReason: string | null;
};

export type OnboarderRow = {
  submissionId: string;
  repName: string;
  repEmail: string;
  repPhone: string;
  status: 'STARTED' | 'COMPLETED';
  createdAt: string;
  completedAt: string | null;
  onboardingLinkId: string;
  contractId: string;
  vendorName: string;
  campaignId: string;
  campaignName: string;
  state: string;
  channel: string | null;
  adminFieldValues: OnboarderFieldValues;
  archivedAt: string | null;
  // D68 — dl/headshot/optional slot presence, so the table can show at a
  // glance which onboarders are missing a headshot/DL and drive the
  // Upload-vs-Replace wording on the new admin control (OnboarderUploadSlotCell).
  uploads: Uploads;
  // D74 (A18 fix) — the list endpoint now surfaces whether a badge has
  // already been issued (derived server-side from RepSubmission.badgeDocumentId,
  // never the raw document id itself — see onboarders.service.ts). Previously
  // this field was declared here but nothing populated it, so
  // OnboardersPage had to fall back to a session-local override that reset
  // on reload; the override still layers ON TOP of this value for the
  // instant post-issue update (see OnboardersPage.tsx), but this is now the
  // durable source of truth.
  hasBadgeOnFile: boolean;
  // veritas-report-receiver (D107): the LATEST received Veritas
  // background-check report for this submission, or null if none has been
  // pushed yet. Metadata only (version + issuedAt) — the PDF bytes are
  // fetched separately, authenticated, via fetchVeritasReportPdf below.
  veritasReport: { version: number; issuedAt: string } | null;
  // S45 background guardrail (docs/contracts/s45-background-guardrail.md §2):
  // the current background-check state for this submission, derived
  // server-side from RepSubmission.backgroundCheckStatus plus the most
  // recent Admin clear/decline. `status: null` means "Pending" (no Veritas
  // report received yet).
  backgroundCheck: BackgroundCheckSummary;
  // S46 (contract §2) — the current AI/human document-check verdict per
  // checked slot ('dl' | 'headshot'; 'optional' is never checked). null on
  // either key means "Not checked" (no row yet, or the row's documentId no
  // longer matches the slot's current document).
  docChecks: DocChecksView;
  // S50 (D144) — true when the nightly job auto-archived this abandoned onboarding.
  autoArchived?: boolean;
  // S50 (D143) — number of training certificates uploaded for this submission.
  trainingCertificateCount?: number;
  // S54 (D150) — "certificate sent" mark on an onboarder. null = not sent (or
  // no certificate on file). Optional so existing fixtures keep compiling.
  certificateSent?: CertificateSentView;
};

// S50 (D143) — metadata for one uploaded training certificate; bytes are
// fetched separately, authenticated, via fetchTrainingCertificate.
export type TrainingCertificateSummary = {
  documentId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
};

export const listTrainingCertificates = (
  submissionId: string,
): Promise<{ certificates: TrainingCertificateSummary[] }> =>
  apiRequest<{ certificates: TrainingCertificateSummary[] }>(
    'GET',
    `/api/onboarders/${encodeURIComponent(submissionId)}/training-certificates`,
  );

export const fetchTrainingCertificate = (submissionId: string, documentId: string): Promise<Blob> =>
  apiGetBlob(
    `/api/onboarders/${encodeURIComponent(submissionId)}/training-certificates/${encodeURIComponent(documentId)}`,
  );

// S54 (D150) — "certificate sent" mark on an onboarder. null = not sent.
export type CertificateSentView = { sentAt: string; sentByName: string | null } | null;
export type CertificateSentResult = { submissionId: string; certificateSent: CertificateSentView };
export type CertSentFilter = 'NOT_SENT' | 'SENT';

export const setCertificateSent = (submissionId: string, sent: boolean): Promise<CertificateSentResult> =>
  apiRequest<CertificateSentResult>('PATCH', `/api/onboarders/${encodeURIComponent(submissionId)}/certificate-sent`, {
    sent,
  });

// S52 (D146) — Admin-only inbox of training certificates that did not match
// exactly one onboarder. Contract: .claude/trnkey-sprint-plan.md section 1.
export type UnmatchedCertificateSuggestion = {
  submissionId: string;
  repName: string;
  badge: string | null;
  repEmail: string;
  campaignName: string;
  status: 'STARTED' | 'COMPLETED';
  matchedOn: Array<'badge' | 'name'>;
};

export type UnmatchedCertificate = {
  id: string;
  uploadedAt: string;
  typedFirstName: string;
  typedLastName: string;
  typedBadge: string | null;
  typedContact: string | null;
  mimeType: string;
  sizeBytes: number;
  fileName: string;
  suggestions: UnmatchedCertificateSuggestion[];
};

export const getUnmatchedCertificateCount = () =>
  apiRequest<{ pending: number }>('GET', '/api/admin/unmatched-certificates/count');

export const listUnmatchedCertificates = () =>
  apiRequest<{ pending: number; items: UnmatchedCertificate[] }>('GET', '/api/admin/unmatched-certificates');

export const fetchUnmatchedCertificateFile = (id: string): Promise<Blob> =>
  apiGetBlob(`/api/admin/unmatched-certificates/${encodeURIComponent(id)}/file`);

export const assignUnmatchedCertificate = (id: string, submissionId: string) =>
  apiRequest<{ ok: true; submissionId: string }>(
    'POST',
    `/api/admin/unmatched-certificates/${encodeURIComponent(id)}/assign`,
    { submissionId },
  );

export const dismissUnmatchedCertificate = (id: string) =>
  apiRequest<{ ok: true }>('POST', `/api/admin/unmatched-certificates/${encodeURIComponent(id)}/dismiss`);

export type OnboardersListResponse = { rows: OnboarderRow[]; total: number; page: number; pageSize: number };

export type OnboardersListFilters = {
  q?: string;
  campaignId?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: string;
  pageSize?: string;
  // server-side view toggle — 'true' returns deleted-only rows, omitted/'false' returns active-only.
  deleted?: string;
  // S45 (contract §2) — 'PENDING' | 'CLEAN' | 'HELD' | 'CLEARED' | 'DECLINED'.
  // 'PENDING' matches backgroundCheckStatus IS NULL server-side; any other
  // value not in BackgroundCheckStatus (plus 'PENDING') 400s.
  backgroundStatus?: string;
  // S46 (contract §2) — matches rows where ANY checked slot has this status.
  // 400 on a value outside DocCheckStatus.
  docCheckStatus?: DocCheckStatus;
  // D147 — YYYY-MM-DD (Central), either optional; filters by training-certificate upload date.
  certUploadedFrom?: string;
  certUploadedTo?: string;
  // D150 (S54) — 'NOT_SENT' | 'SENT', absent = Any. Combines with certUploaded* above.
  certSent?: CertSentFilter;
};

export const getOnboarderFieldSet = () =>
  apiRequest<OnboarderFieldSetResponse>('GET', '/api/onboarder-field-set');

export const putOnboarderFieldSet = (fields: OnboarderField[]) =>
  apiRequest<OnboarderFieldSetResponse>('PUT', '/api/onboarder-field-set', { fields });

export const listOnboarders = (filters: OnboardersListFilters = {}) =>
  apiRequest<OnboardersListResponse>('GET', `/api/onboarders${buildListQuery(filters)}`);

export const updateOnboarderAdminFields = (submissionId: string, adminFieldValues: OnboarderFieldValues) =>
  apiRequest<{ submissionId: string; adminFieldValues: OnboarderFieldValues }>(
    'PATCH',
    `/api/onboarders/${encodeURIComponent(submissionId)}/admin-fields`,
    { adminFieldValues },
  );

export const deleteOnboarder = (submissionId: string) =>
  apiRequest<OnboarderRow>('PATCH', `/api/onboarders/${encodeURIComponent(submissionId)}/delete`);

export const restoreOnboarder = (submissionId: string) =>
  apiRequest<OnboarderRow>('PATCH', `/api/onboarders/${encodeURIComponent(submissionId)}/restore`);

// D-Move — "Move to a different vendor": destination options are active
// onboarding links only; moving re-points where the rep appears going
// forward, but already-signed documents stay with the original vendor.
export type OnboardingLinkTarget = { onboardingLinkId: string; label: string };

export const getOnboardingLinkTargets = () =>
  apiRequest<OnboardingLinkTarget[]>('GET', '/api/onboarders/onboarding-links');

// S46b (founder re-scope 2026-09-25) — approveDocCheck/rejectDocCheck
// removed with the review UI: the AI verdict is now the only admin-facing
// surface (a label next to each DL/Headshot). No client-side action mutates
// a DocCheckSummary anymore.

// S46 (contract §7) — campaign notification settings (reminders + templates).
// S46b (founder re-scope 2026-09-25) — no more per-document reject/reupload
// flow and no more "all set" congratulations message: DOC_REJECTED,
// REMINDER_REUPLOAD and REP_ALL_SET are gone. Only REMINDER_INCOMPLETE (rep
// hasn't finished) and TEAM_PROFILE_COMPLETE (team alert) remain — see
// NotificationSettingsCard.tsx's RemainingKind, which this type now matches.
export type TemplateKind = 'REMINDER_INCOMPLETE' | 'TEAM_PROFILE_COMPLETE';

export type NotificationSettingsView = {
  campaignId: string;
  enabled: boolean;
  enabledAt: string | null;
  reminderIntervalDays: number;
  reminderMaxCount: number;
  templates: Record<
    TemplateKind,
    {
      smsBody: string | null;
      emailSubject: string;
      emailBody: string;
      isCustom: { smsBody: boolean; emailSubject: boolean; emailBody: boolean };
    }
  >;
  defaults: Record<TemplateKind, { smsBody: string | null; emailSubject: string; emailBody: string }>;
  placeholders: Record<TemplateKind, string[]>;
  channels: { smsConfigured: boolean; emailConfigured: boolean; aiChecksEnabled: boolean };
  updatedAt: string | null;
  updatedByName: string | null;
};

export type NotificationSettingsInput = {
  enabled: boolean;
  reminderIntervalDays: number;
  reminderMaxCount: number;
  templates?: Partial<
    Record<TemplateKind, { smsBody?: string | null; emailSubject?: string | null; emailBody?: string | null }>
  >;
};

export const getNotificationSettings = (campaignId: string) =>
  apiRequest<NotificationSettingsView>('GET', `/api/campaigns/${encodeURIComponent(campaignId)}/notification-settings`);

export const putNotificationSettings = (campaignId: string, input: NotificationSettingsInput) =>
  apiRequest<NotificationSettingsView>(
    'PUT',
    `/api/campaigns/${encodeURIComponent(campaignId)}/notification-settings`,
    input,
  );

export const moveOnboarderToLink = (submissionId: string, onboardingLinkId: string) =>
  apiRequest<OnboarderRow>(
    'PATCH',
    `/api/onboarders/${encodeURIComponent(submissionId)}/onboarding-link`,
    { onboardingLinkId },
  );

// S38-vendor-move (bulk) — move MANY onboarders to a different vendor in one
// action (client request from Rob). All-or-nothing on the server: either
// every id in submissionIds moves, or none do — see
// OnboarderMoveBulkService for the validation-before-write contract.
export type BulkMoveResult = { movedCount: number; targetOnboardingLinkId: string };

export const bulkMoveOnboardersToLink = (submissionIds: string[], targetOnboardingLinkId: string) =>
  apiRequest<BulkMoveResult>(
    'PATCH',
    '/api/onboarders/bulk/onboarding-link',
    { submissionIds, targetOnboardingLinkId },
  );

// ── conditional-offer-record — Spark-only compliant background-check gate ───
// Mirrors apps/api/src/conditional-offer/conditional-offer.service.ts's
// ConditionalOfferStatus exactly. @Roles('Admin','Staff') + scope-asserted on
// the server; the server always resolves recordedByUserId from the JWT
// (@CurrentUser()), never from anything the client sends — see that file's
// header on why that identity is the compliance control. Restricted to Spark
// campaigns server-side (409 NOT_SPARK_CAMPAIGN) — mirror that gate in the UI
// with isSparkCampaignName below rather than relying on the 409 alone.
export type ConditionalOfferStatus = {
  submissionId: string;
  recordedAt: string | null;
  recordedByUserId: string | null;
  recordedByUserName: string | null;
  attestationText: string | null;
};

// Mirrors apps/api/src/conditional-offer/spark-campaign.helper.ts's
// isSparkCampaign exactly (case-insensitive "Spark " prefix match) — kept in
// sync by hand, same convention as this file's other server-type mirrors
// (e.g. BadgePlacementSlot above). There is no Spark/Wave flag in the data
// model; this is a deliberate, documented name-matching gate, not a real
// campaign-type field. See the server helper's own header for the rationale
// and the TODO to replace this with a real Campaign.type column.
export function isSparkCampaignName(campaignName: string): boolean {
  return /^spark\s/i.test(campaignName.trim());
}

export const getConditionalOfferStatus = (submissionId: string) =>
  apiRequest<ConditionalOfferStatus>('GET', `/api/conditional-offer/${encodeURIComponent(submissionId)}`);

// N+1 fix — OnboardersPage used to call getConditionalOfferStatus once per
// visible row, which blew through this controller's 10/min throttle on any
// page past 10 Spark rows (ThrottlerException surfaced in the column for
// rows 11+). This is the same read, batched: one call per page render for
// every visible submissionId instead of one per row. Body/response shape
// mirrors BulkConditionalOfferStatusDto/conditional-offer.controller.ts's
// bulk-status route exactly (1-500 ids in, ConditionalOfferStatus[] out).
// Ids that don't exist (or aren't Spark/in scope) are just omitted from the
// response array — this is a list read, not a per-id assertion — so callers
// index the result by submissionId rather than assuming positional/1:1
// alignment with the request array.
export const getBulkConditionalOfferStatus = (submissionIds: string[]) =>
  apiRequest<ConditionalOfferStatus[]>('POST', '/api/conditional-offer/bulk-status', { submissionIds });

export const recordConditionalOffer = (submissionId: string, attestationText?: string) =>
  apiRequest<ConditionalOfferStatus>(
    'POST',
    `/api/conditional-offer/${encodeURIComponent(submissionId)}`,
    attestationText !== undefined ? { attestationText } : undefined,
  );

// Always 409s today (NO_CONDITIONAL_OFFER or VERITAS_RUN_NOT_ENABLED) — the
// real Veritas run integration is intentionally not wired yet. Typed `never`
// to match the server's response shape (it never resolves 200); callers
// always hit the catch branch. See ConditionalOfferService.requestRun.
export const runConditionalOfferBackgroundCheck = (submissionId: string) =>
  apiRequest<never>('POST', `/api/conditional-offer/${encodeURIComponent(submissionId)}/run`);

// S45 (contract §5) — Admin-only bulk attestation. All-or-nothing on the
// server: any scope miss / unknown id / non-Spark campaign in the batch
// writes nothing (404 SUBMISSION_NOT_FOUND or 409 NOT_SPARK_CAMPAIGN).
// Response is ConditionalOfferStatus[] in de-duplicated input order.
export const recordConditionalOffersBulk = (submissionIds: string[], attestationText?: string) =>
  apiRequest<ConditionalOfferStatus[]>(
    'POST',
    '/api/conditional-offer/bulk-record',
    attestationText !== undefined ? { submissionIds, attestationText } : { submissionIds },
  );

// ── background-review — Admin review of a HELD/DECLINED background check ───
// Mirrors docs/contracts/s45-background-guardrail.md §3/§4 exactly. Both
// routes are Admin-only, 10/min throttled server-side, and respond with
// { submissionId } & BackgroundCheckSummary. 409 BACKGROUND_NOT_REVIEWABLE
// when the current status isn't in the allowed-from set for that transition
// — callers branch on err.code, same convention as NOT_SPARK_CAMPAIGN above.
// `reportVersion` (S45 veritas-results-guardrail) pins the decision to the
// Veritas report the Admin actually reviewed; the server 409s with
// BACKGROUND_REPORT_STALE + latestReportVersion when a newer report arrived
// after the dialog opened, same branch-on-err.code convention.
export type BackgroundReviewResult = { submissionId: string } & BackgroundCheckSummary;

export const clearBackgroundHold = (submissionId: string, reportVersion: number) =>
  apiRequest<BackgroundReviewResult>(
    'POST',
    `/api/background-review/${encodeURIComponent(submissionId)}/clear`,
    { reportVersion },
  );

export const declineBackground = (submissionId: string, reason: string, reportVersion: number) =>
  apiRequest<BackgroundReviewResult>(
    'POST',
    `/api/background-review/${encodeURIComponent(submissionId)}/decline`,
    { reason, reportVersion },
  );

// S31/D37 — 'file'-type admin field: attach/replace/remove/view ONE file on a
// rep's row (e.g. a Veritas background-check PDF). Multipart upload mirrors
// manualUploadContract above (field name 'file'); download mirrors
// fetchSubmissionUpload's authed-blob pattern (PORTAL-ONLY — no public link
// exists for these bytes, so a bare <a href> would 401 the same way).
export const uploadAdminFieldFile = (submissionId: string, fieldKey: string, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return apiPostForm<{ submissionId: string; fieldKey: string; documentId: string }>(
    `/api/onboarders/${encodeURIComponent(submissionId)}/admin-fields/${encodeURIComponent(fieldKey)}/file`,
    form,
  );
};

export const downloadAdminFieldFile = (submissionId: string, fieldKey: string) =>
  apiGetBlob(`/api/onboarders/${encodeURIComponent(submissionId)}/admin-fields/${encodeURIComponent(fieldKey)}/file`);

export const removeAdminFieldFile = (submissionId: string, fieldKey: string) =>
  apiRequest<{ submissionId: string; fieldKey: string }>(
    'DELETE',
    `/api/onboarders/${encodeURIComponent(submissionId)}/admin-fields/${encodeURIComponent(fieldKey)}/file`,
  );

// D68 — admin set/replace of the rep's 'dl' or 'headshot' upload slot,
// regardless of submission status. Multipart shape mirrors
// uploadAdminFieldFile above (field name 'file'); the server rejects any
// slot other than 'dl'/'headshot' with a 400 INVALID_UPLOAD_SLOT.
export type UploadSlotReplaceResult = { submissionId: string; slot: 'dl' | 'headshot'; documentId: string; previousDocumentId: string | null };

export const replaceUploadSlot = (submissionId: string, slot: 'dl' | 'headshot', file: File) => {
  const form = new FormData();
  form.append('file', file);
  return apiPostForm<UploadSlotReplaceResult>(`/api/onboarders/${encodeURIComponent(submissionId)}/uploads/${encodeURIComponent(slot)}`, form);
};

// ── Onboarder xlsx import (frozen contract) — dryRun previews, commit applies.
// Same response shape for both; `applied` is always 0 on a dry run.

export type OnboarderImportChange = {
  submissionId: string;
  repName: string;
  changedKeys: string[];
  before: Record<string, string>;
  after: Record<string, string>;
};

export type OnboarderImportRowError = { row: number; submissionId: string | null; code: string; message: string };

/** A configured admin field-set key that collides with the exporter's own fixed
 * column names (e.g. a field literally keyed "Status") — such a field can never
 * round-trip through export/import, computed from the field set itself so it's
 * reported even on a file that doesn't contain the column. */
export type OnboarderImportFixedColumnCollision = string;

/** A writable column that was skipped for one submission only because it collides
 * with that submission's own intake answer (intake wins for that row) — distinct
 * from `ignoredColumns`, which is never writable for any row. */
export type OnboarderImportIntakeCollision = { submissionId: string; columns: string[] };

/** The same Submission ID appeared on more than one row — first occurrence
 * (workbook order) wins. `sheets` names EVERY occurrence's sheet in order, so
 * its length is the occurrence count: identical entries mean the duplicate was
 * on the same sheet, distinct entries mean it spanned sheets. */
export type OnboarderImportDuplicateSubmissionId = { submissionId: string; sheets: string[] };

export type OnboarderImportResult = {
  dryRun: boolean;
  totalRows: number;
  matched: number;
  unmatched: number;
  changed: number;
  applied: number;
  ignoredColumns: string[];
  writableColumns: string[];
  fixedColumnCollisions: OnboarderImportFixedColumnCollision[];
  intakeCollisions: OnboarderImportIntakeCollision[];
  /** Stored admin keys no longer in the current field set, dropped from the write — only reported for rows that actually had a sheet-driven change. */
  droppedLegacyKeys: string[];
  /** Sheets with no Submission ID column — skipped, not fatal (the file as a whole is only rejected when NO sheet has the column). */
  skippedSheets: string[];
  duplicateSubmissionIds: OnboarderImportDuplicateSubmissionId[];
  changes: OnboarderImportChange[];
  changesTruncated?: boolean;
  errors: OnboarderImportRowError[];
};

/** Admin-only, 25MB limit, throttled 10/min. Caller keeps the File in state and posts it twice (dryRun=true, then dryRun=false) — no re-prompt between steps. */
export const importOnboarders = (file: File, dryRun: boolean): Promise<OnboarderImportResult> => {
  const form = new FormData();
  form.append('file', file);
  return apiPostForm<OnboarderImportResult>(`/api/onboarders/import?dryRun=${dryRun}`, form);
};

// ── D78: bulk CREATE onboarders from a vendor spreadsheet (frozen contract per
// SPEC-D78 as of this writing — the backend agent owns the exact response field
// names and may land a slightly different shape; every wire type below is
// grouped in this one block so a mismatch is a one-spot fix). Distinct from
// importOnboarders above: that endpoint only ever UPDATES admin fields on
// people already in the portal, this one CREATES new onboarder records —
// same two-step dryRun-then-commit shape, same File-kept-in-state contract.

export type BulkCreateRowError = { row: number; code: string; message: string };

/** One person who would be (or was) created. Field names mirror the API's
 * BulkCreatePreviewRow exactly (onboarder-bulk-create.types.ts) — the server
 * deliberately returns ONLY the three identity fields it derived, never the
 * raw sheet cells, so there is no arbitrary uploaded-column surface to render
 * (and therefore nothing here for SENSITIVE_LABEL_RE to redact: SSN/DOB are
 * excluded server-side before a data row is ever read). */
export type BulkCreatePerson = { row: number; repName: string; repEmail: string };

/** MUST match the API's BulkCreateResult field-for-field
 * (apps/api/src/onboarders/onboarder-bulk-create.types.ts). Note `reused`, not
 * `created`, on the three chain entries: the server reports whether it REUSED an
 * existing vendor/contract/link, so a UI reading these as `created` would state
 * the exact opposite of the truth on the one notice that tells an admin whether
 * a new vendor is about to exist. */
export type BulkCreateResult = {
  dryRun: boolean;
  headerRowNumber: number;
  totalRows: number;
  wouldCreate: number;
  created: number;
  skippedExisting: number;
  /** Of skippedExisting, how many were dropped because they are already on this
   * campaign under a DIFFERENT vendor (vs already on this vendor's own link). */
  skippedOnCampaign: number;
  /** Columns dropped from the sheet before any processing because they look
   * like SSN/DOB — never an error, just a reassurance note in the UI. */
  excludedSensitiveColumns: string[];
  vendor: { id: string; companyName: string; reused: boolean };
  contract: { id: string; reused: boolean };
  onboardingLink: { id: string; reused: boolean };
  preview: BulkCreatePerson[];
  /** Backend caps the `preview` list at 500 — true means more rows would be
   * created than are shown here. */
  previewTruncated?: boolean;
  errors: BulkCreateRowError[];
};

export type BulkCreateParams = { campaignId: string; vendorName: string; vendorCode?: string; skipExistingOnCampaign?: boolean };

/** Admin-only. Caller keeps the File in state and posts it twice (dryRun=true preview,
 * then dryRun=false commit) — no re-prompt between steps, mirroring importOnboarders. */
export const bulkCreateOnboarders = (file: File, dryRun: boolean, params: BulkCreateParams): Promise<BulkCreateResult> => {
  const form = new FormData();
  form.append('file', file);
  const qs = buildListQuery({
    dryRun: String(dryRun),
    campaignId: params.campaignId,
    vendorName: params.vendorName,
    vendorCode: params.vendorCode || undefined,
    // Only sent when explicitly false — the server defaults it to true, so
    // omitting it keeps the safe behaviour.
    skipExistingOnCampaign: params.skipExistingOnCampaign === false ? 'false' : undefined,
  });
  return apiPostForm<BulkCreateResult>(`/api/onboarders/bulk-create${qs}`, form);
};

// ── D90: "Send to Veritas" — Admin-only, three-step handoff
// (local preview -> ask Veritas -> send). All three endpoints take the same
// body shape and are frozen per veritas.controller.ts/veritas-handoff.service.ts
// on the API side; the shapes below mirror those files field-for-field.

/** Browser-safe identity for one person about to be sent — never carries SSN
 * (VeritasHandoffService.preview builds this from an allowlisted projection
 * of the export row, not the raw sheet). */
export type VeritasHandoffPerson = {
  submissionId: string;
  repName: string;
  repEmail: string;
  vendor: string;
  campaign: string;
  dob: string;
};

export type VeritasHandoffPreview = {
  rowCount: number;
  people: VeritasHandoffPerson[];
  fileDigest: string;
  columns: string[];
  /** Submission ids that could not be assembled into a row — shown so the
   * admin knows who is being silently left out of the batch. */
  omittedIds: string[];
};

/** Veritas's own per-row verdict from their import/preview route — only
 * "importable" rows are eligible to actually be sent. */
export type VeritasRemoteRowState = 'importable' | 'invalid' | 'duplicate';

export type VeritasRemoteRow = {
  rowNumber: number;
  state: VeritasRemoteRowState;
  reasons: string[];
};

/** Success shape only — the controller throws a 409 (surfaced as ApiError)
 * when Veritas itself reports `{ ok: false }`, so callers branch on ApiError,
 * not on an `ok` field, mirroring how the API maps that case. */
export type VeritasRemotePreviewResult = {
  sheet: string;
  fileDigest: string;
  totals: { parsed: number; importable: number; invalid: number; duplicate: number };
  rows: VeritasRemoteRow[];
};

export type VeritasHandoffSendResult = { sent: true; rowCount: number; fileDigest: string };

export const previewVeritasHandoff = (submissionIds: string[]) =>
  apiRequest<VeritasHandoffPreview>('POST', '/api/veritas/handoff/preview', { submissionIds });

export const previewVeritasHandoffRemote = (submissionIds: string[]) =>
  apiRequest<VeritasRemotePreviewResult>('POST', '/api/veritas/handoff/remote-preview', { submissionIds });

export const sendVeritasHandoff = (submissionIds: string[]) =>
  apiRequest<VeritasHandoffSendResult>('POST', '/api/veritas/handoff/send', { submissionIds });

// S47 (contracts-ui): vendor contacts export for one campaign — .xlsx blob plus
// the server's Content-Disposition filename when present. Admin + Staff.
export const downloadCampaignVendorContactsExport = (
  campaignId: string,
): Promise<{ blob: Blob; filename?: string }> =>
  apiGetBlobWithFilename(`/api/exports/campaigns/${encodeURIComponent(campaignId)}/vendor-contacts`);

// D145: vendors export (.xlsx) honoring the list's current filters. Admin only.
export const downloadVendorsExport = (params: {
  includeArchived?: boolean;
  q?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<{ blob: Blob; filename?: string }> => {
  const qs = new URLSearchParams();
  if (params.includeArchived) qs.set('includeArchived', 'true');
  if (params.q?.trim()) qs.set('q', params.q.trim());
  if (params.dateFrom) qs.set('dateFrom', params.dateFrom);
  if (params.dateTo) qs.set('dateTo', params.dateTo);
  const s = qs.toString();
  return apiGetBlobWithFilename(`/api/exports/vendors${s ? `?${s}` : ''}`);
};
